/**
 * The tree's named operations — the only writes there are, and the constructors that mint identity.
 *
 * Document-model ADR §5b: **no component reads or writes the draft tree directly.** Mutation happens
 * exclusively through a named set of operations, each of which owns the invariants that operation can
 * break. Zustand's idiom invites the opposite — a component selecting `phases[i].days[j]` and
 * mutating in place — and that idiom is what this module rules out: it makes the tree SHAPE the
 * interface, so every caller has to learn the whole document and every invariant becomes a
 * convention rather than a guarantee.
 *
 * Two consequences make the small discipline worth it. The clone rule (§3) stops being something a
 * future contributor has to remember, because the only way to construct a copied day or exercise is
 * a factory that mints a fresh `slotToken`. And the ordering rule (§10) gets one owner: **positions
 * are assigned inside the operations and never by a caller.**
 *
 * ── Two calling conventions, and the reason they differ ──────────────────────────────────────
 * The MUTATORS take a mutable tree and return nothing; the CONSTRUCTORS are pure and return a new
 * node. That is not inconsistency, it is what each one is for.
 *
 * A mutator runs inside an Immer producer, and it has to mutate in place for the recorded patch to
 * be proportional to the edit rather than to the tree. ADR §5 chose patch-based undo over snapshots
 * precisely for that: a producer that RETURNED a whole new tree would record one root-level
 * `replace` patch per keystroke, which is a snapshot with extra steps. `Draft<PlanDraft>` is a
 * type-only import — Immer is a pure data library and `model/`'s bans are on React, the DOM, query
 * hooks and i18next, none of which this is.
 *
 * A constructor mints identity and has no existing node to mutate, so it returns one. Its tests call
 * it directly; a mutator's tests wrap it in `produce`, which is also exactly how the store calls it.
 *
 * ── Addressing is by slot token, never by path ───────────────────────────────────────────────
 * A day and an exercise are addressed by their `slotToken` alone, with no phase or day index, because
 * a token is unique across the WHOLE plan — the backend refuses a submission where two share one
 * (`duplicate_slot_token`), so plan-wide uniqueness is a guarantee rather than an assumption. That
 * also makes an address survive an edit that reshapes the arrays around its target, which a path
 * cannot, and it is the same durability the ADR §6 note asks a future crash-recovery command log to
 * have.
 *
 * A SET has no identity at all (§1), so it is addressed by its exercise's token plus its 1-based
 * position — the same positional coordinate the wire's overrides use.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`.
 */

import type { Draft } from 'immer';

import type { MeasurementFieldKey } from '~/shared/measurement';

import {
  createDay,
  createExercise,
  createPhase,
  createSet,
  type AuthorableExerciseRole,
  type ExercisePick,
} from './draftFactory';
import { rebaseToCycle } from './cycleRebase';
import { writeBand } from './progressionBand';
import { ruleOnColumnPatch } from './progressionRulePatches';
import { exposureOffsetOf, findExerciseLifespan } from './cycleLifespan';
import {
  phaseAtCycle,
  restoredDayWindow,
  type DayCycleRemoval,
  type DayCycleScope,
  type DayCycleWindow,
} from './dayCycleWindow';
import { exerciseCycleWindow, phaseStartCycles } from './cycleWindow';
import { libraryDropRefusal, type DropRefusal } from './dropGates';
import { consumedCycleCount, isLivePlan } from './livePlanLocks';
import { ADDED_PHASE_CYCLES, addPhaseRefusal, phaseEditGates } from './phaseEdits';
import { planCategoryKey, planCategoryToApi, type PlanCategoryKey } from './planCategory';
import { removeColumnValues } from './exerciseColumns';
import {
  clampDayRest,
  dayRestSeconds,
  deriveRestColumns,
  overridesDayRest,
  restampExerciseRest,
} from './dayRest';
import {
  COLLECTION_CAPS,
  DAY_OF_WEEK_RANGE,
  MAX_ABSOLUTE_CYCLE,
  REST_DAYS_AFTER_RANGE,
  TEXT_LIMITS,
  clampToPlanCycles,
} from './planCaps';
import type {
  DayDraft,
  ExerciseDraft,
  PhaseDraft,
  PhaseType,
  PlanDraft,
  ProgressionOverride,
  SetDraft,
} from './planDraft.types';
import { normalizeSetLoad, PRESCRIPTION_MODE } from './prescriptionMode';
import {
  axisBaseOf,
  axisForExercise,
  axisForField,
  normaliseTypedValue,
  PERCENT_AXIS,
  type ProgressionAxis,
} from './progressionAxis';
import type { ProgressionRule, ProgressionRules } from './progressionRules';
import {
  clearColumnPins,
  clearOverride,
  commitBackoffPercent,
  chainAnchorOf,
  pinnedColumns,
  projectFromAnchor,
  ruleFromExercise,
  upsertOverride,
} from './progressionMatrix';
import { countsAsWork, isWarmupRole } from './setCounting';
import { dayCard } from './weekProjection';
import {
  backoffBlockHeal,
  backoffPick,
  derivesLoadInSession,
  isTopSet,
  TOP_SET_TYPE,
  WORKING_SET_TYPE,
} from './setShape';
import { amrapCapExclusivityPatch, setTypePatch } from './setTypeChange';
import { newSetLocalId, newSlotToken } from './slotToken';

/** A mutable view of the tree — an Immer draft in production, a `produce` recipe's argument in tests. */
type MutablePlan = Draft<PlanDraft>;

/** Every day in the plan, in tree order. Phase boundaries do not matter to a token lookup. */
function* everyDay(draft: MutablePlan): Generator<Draft<DayDraft>> {
  for (const phase of draft.phases) {
    for (const day of phase.days) yield day;
  }
}

function findDay(draft: MutablePlan, slotToken: string): Draft<DayDraft> | null {
  for (const day of everyDay(draft)) {
    if (day.slotToken === slotToken) return day;
  }
  return null;
}

/**
 * The day an EXERCISE sits in, and the phase above it.
 *
 * Located by scanning rather than threaded in as a parameter, because every caller addresses an
 * exercise by its token alone and has no path to hand over. Several operations genuinely need the
 * parent: the rest rules are day-scoped, superset groups are day-scoped, and the last-exercise guard
 * is phase-scoped.
 */
interface ExerciseSite {
  readonly phase: Draft<PhaseDraft>;
  readonly day: Draft<DayDraft>;
  readonly exercise: Draft<ExerciseDraft>;
  /** The exercise's position within its day. */
  readonly index: number;
}

function findExerciseSite(draft: MutablePlan, slotToken: string): ExerciseSite | null {
  for (const phase of draft.phases) {
    for (const day of phase.days) {
      const index = day.exercises.findIndex((exercise) => exercise.slotToken === slotToken);
      const exercise = day.exercises[index];
      if (exercise !== undefined) return { phase, day, exercise, index };
    }
  }
  return null;
}

/** A day and the phase above it. The moves need the phase: a slot lives inside one. */
interface DaySite {
  readonly phase: Draft<PhaseDraft>;
  readonly day: Draft<DayDraft>;
}

function findDaySite(draft: MutablePlan, slotToken: string): DaySite | null {
  for (const phase of draft.phases) {
    for (const day of phase.days) {
      if (day.slotToken === slotToken) return { phase, day };
    }
  }
  return null;
}

function findExercise(draft: MutablePlan, slotToken: string): Draft<ExerciseDraft> | null {
  for (const day of everyDay(draft)) {
    for (const exercise of day.exercises) {
      if (exercise.slotToken === slotToken) return exercise;
    }
  }
  return null;
}

/**
 * The same lookup, READ-ONLY — for the store's narrow selectors rather than for a mutator.
 *
 * A separate function rather than a widened `findExercise`, because the two have opposite
 * obligations: a mutator's lookup must hand back a node the caller may write to, and a selector's
 * must hand back one it may not. Returning `ExerciseDraft` (every field `readonly`) rather than
 * `Draft<ExerciseDraft>` is what makes "a component mutated the tree behind the store's back" a type
 * error instead of an Immer auto-freeze exception at runtime.
 *
 * Returns the NODE, whose identity Immer's structural sharing preserves across every edit that did
 * not touch it — which is the whole reason a selector may return it at all.
 */
export function findExerciseNode(
  draft: PlanDraft | null,
  slotToken: string,
): ExerciseDraft | null {
  if (draft === null) return null;
  for (const phase of draft.phases) {
    for (const day of phase.days) {
      for (const exercise of day.exercises) {
        if (exercise.slotToken === slotToken) return exercise;
      }
    }
  }
  return null;
}

/**
 * Rename the workout a day holds.
 *
 * The name the v3 canon prints on a session card is the SESSION's ("Push A"), not the day's — the
 * column header already carries the weekday — so this writes `sessionName` and leaves `name` alone.
 * The two are different fields on the wire and both round-trip; conflating them would rename the
 * scheduling unit when the trainer meant the workout.
 *
 * **Whitespace collapses to `null`.** `PostSessionTemplateInput.Name` is `[MinLength(1)]`, so an
 * empty string is a 400, and `draftToRequest` falls back through the day's name to a constant. A
 * trainer who clears the field is saying "no name", and `null` is how this tree spells that — one
 * representation, decided where the value enters, exactly as the load seam decides it.
 *
 * An unknown token is a no-op rather than an error. The day may have been removed by an undo between
 * the trainer pressing a key and this running, and a rename that finds nothing has nothing to fix.
 */
export function renameSession(draft: MutablePlan, daySlotToken: string, name: string): void {
  const day = findDay(draft, daySlotToken);
  if (day === null) return;
  const trimmed = name.trim();
  day.sessionName = trimmed.length > 0 ? trimmed : null;
}

/**
 * The set fields this operation may write.
 *
 * `setType` is deliberately NOT among them. Changing a set's type changes which load fields it may
 * carry — a back-off may not author a weight, a percent-mode exercise may not author one either —
 * and mobile funnels every type change through a normalisation plus a back-off block repair
 * (`normalizeSetLoad`, `backoffBlockHeal`). Those arrive with the set table in PR-B2, which is the
 * first surface that can author a type. Admitting `setType` here without them would let a set keep
 * the previous role's authored load, invisible behind the cell that replaced it, until the save 400s
 * on a field nothing rendered.
 *
 * `note` IS here, and it is the mocked G4 field: it lives in the draft, and `draftToRequest` drops it
 * on the wire.
 */
export type SetFieldPatch = Partial<
  Pick<
    SetDraft,
    | 'prescribedWeight'
    | 'prescribedReps'
    | 'prescribedRpe'
    | 'prescribedRir'
    | 'prescribedRestTime'
    | 'prescribedDurationSeconds'
    | 'prescribedDistanceMeters'
    | 'prescribedRounds'
    | 'prescribedPercent'
    | 'prescribedTempo'
    | 'prescribedSpeed'
    | 'note'
  >
>;

/** Where one set lives: its exercise's durable token, and its 1-based position within that exercise. */
export interface SetAddress {
  readonly exerciseSlotToken: string;
  /** 1-based, matching `SetDraft.setIndex` and the coordinate the wire's overrides address by. */
  readonly setIndex: number;
}

/**
 * Where one MATRIX cell lives — a row, an absolute cycle, and the two things that decide what
 * writing to it means.
 *
 * `cycleIndex` is ABSOLUTE (1..Σ cycleCount), exactly as the wire's `PostSetOverrideInput` is. It is
 * accompanied by `baseCycle` — the exercise's own first active cycle — rather than the operation
 * re-deriving it, because deriving it means walking the phases to accumulate their spans, and doing
 * that inside a mutator would put the origin rule in a second place. `cycleWindow.ts` owns it, the
 * matrix already asked it, and passing the answer along is what keeps the two from drifting.
 *
 * **`field` is the COLUMN THE GRID IS DRAWING, and it is not optional reasoning.** An earlier
 * revision carried no column at all: an axis had arrived from a caller, a surface previewing an
 * unapplied rule passed its own, and a cell click then wrote an override into a field the grid was
 * not showing — switch to RPE, pin a cell, press Anuluj, and a stray `prescribedRpe` stayed behind
 * on a weight rule, which the backend accepts and the trainer never sees again. Deriving the axis
 * from the exercise fixed the stray and cost the grid its freedom to show any column but the one the
 * plan already progresses.
 *
 * Naming the column fixes both halves, because it is the SAME answer the grid was drawn from: the
 * rule sheet passes the column its chips have selected and `progressionMatrixView` the same one, so
 * a pin lands in the field the number it replaced was printed in. Omitted, the exercise's own opening
 * column stands — which is what the set table's per-cycle cell means, since `pinField` is that axis
 * by construction.
 */
export interface MatrixCellAddress {
  /** 1-based positional row, warm-ups included — the coordinate the wire addresses cells by. */
  readonly setIndex: number;
  /** The absolute cycle this column is. */
  readonly cycleIndex: number;
  /** The exercise's first active cycle. The column AT it is the authored base and is never pinnable. */
  readonly baseCycle: number;
  /** The column the grid drew the cell in. See above; omitted means the exercise's own. */
  readonly field?: MeasurementFieldKey;
}

/**
 * Write one or more prescription values on one set.
 *
 * The set is found by POSITION within its exercise, not by its own `setIndex` field, and the two are
 * asserted to agree before anything is written. They can only disagree on a tree that a previous
 * operation left mis-numbered, and writing to whichever of the two happened to be right would make
 * that bug silent for one more edit; refusing is what makes it findable.
 *
 * **The AMRAP at-most-one-cap rule IS applied here**, as of PR-B2C. PR-B1 left it out because
 * nothing could yet reach two effort cells at once; the set table can, so committing a value in one
 * effort column now clears the other (`amrapCapExclusivityPatch`). Enforcing it at the PATCH layer
 * rather than by locking a cell is what keeps both cells editable — and clearing a cap never touches
 * the other, so a bare-floor AMRAP stays legal. The wire half (`amrapShippedCaps`) stays too: it is
 * the guarantee a save can never ship both, whatever the draft is holding mid-edit.
 */
export function setSetField(draft: MutablePlan, address: SetAddress, patch: SetFieldPatch): void {
  const exercise = findExercise(draft, address.exerciseSlotToken);
  if (exercise === null) return;
  const set = exercise.sets[address.setIndex - 1];
  if (set === undefined || set.setIndex !== address.setIndex) return;
  Object.assign(set, amrapCapExclusivityPatch(set, patch));
}

/**
 * A copied set: every prescribed value, and a FRESH `localId`.
 *
 * Nothing about a set is identity on the wire — it has none (§1) — so the values copy wholesale.
 * The local id is the exception and it is minted rather than copied, for the same reason a cloned
 * day mints a slot token: the copy is a different row on screen, and two rows sharing a React key
 * is the defect this id exists to prevent, not one it is allowed to introduce.
 */
function cloneSet(set: SetDraft): SetDraft {
  return { ...set, localId: newSetLocalId() };
}

/**
 * A copy of the progression map, **two levels deep and by value**.
 *
 * Both levels matter: sharing the MAP makes a rule authored on the copy reach the original, and
 * sharing a rule DOCUMENT does the same one step down. A clone is a new exercise that happens to
 * start with the same prescription, never the same prescription seen twice.
 */
function cloneProgressionRules(rules: ProgressionRules): ProgressionRules {
  const copy: Partial<Record<MeasurementFieldKey, ProgressionRule>> = {};
  for (const [field, rule] of Object.entries(rules)) copy[field as MeasurementFieldKey] = { ...rule };
  return copy;
}

/**
 * A MOVED exercise — the row `moveExercise` carries into another day of its own phase. A **NEW
 * SLOT**, and the trainer's own cycle window intact.
 *
 * ── The window is what separates a move from a copy, and getting it wrong is a WIPE ───────────
 * `retirementCycle` and `excludedCycles` are absolute cycle coordinates the trainer authored to say
 * "stop after this week" and "not that one". The backend re-materialises the client's not-started
 * workouts from exactly them and reads an absent bound as "the whole host", so answering a MOVE the
 * copy's way — dropping both — widens the exercise back over every week the trainer cut. That is
 * training the client never asked for, minted by a gesture that said nothing about weeks.
 *
 * The precedent is one level up and already shipped: `cloneDay` KEEPS the day's authored window,
 * because which cycles a thing runs in is part of what the trainer BUILT rather than a fact about
 * its history. A moved exercise is that same prescription under a new parent, so it keeps both.
 * `cloneExercise` below is this function plus the one line that answers the other way, which is why
 * they are two named functions rather than one carrying a flag.
 *
 * Document-model ADR §3 governs everything else here, and each part prevents a specific silent
 * corruption:
 *
 * · **A fresh `slotToken` is minted.** Echoing the source's would either 400 outright with
 *   `duplicate_slot_token` (if the source is still in the same submission) or — worse, once the
 *   source has been deleted in the same edit — let the new row silently INHERIT the original's
 *   progression chain and its `PreviousExecution` / `Amrap` history. The trainer would have no
 *   reason to believe it has any.
 * · **`activationCycle` is dropped.** It is server-owned, and a new slot is a first exposure: the
 *   backend assigns it at the currently-open cycle on save. Carrying the source's would materialise
 *   the row's prescriptions from the wrong baseline.
 * · **`progressionOverrides` are cloned BY VALUE.** Copying the array reference would make the two
 *   exercises share one override list, so pinning a cell on one would move the cell on the other —
 *   a change the trainer never made, in a phase they may not even be looking at.
 * · **`serverCycles` is dropped.** It is the server's resolution of a row that does not exist yet.
 * · **`progressionRules` is copied BY VALUE, two levels deep**, and `realMaxMultiplier` /
 *   `realMaxSource` come across as the plain numbers they are. The rule is the PRESCRIPTION — the
 *   thing the trainer copied or moved the exercise for — so it travels, unlike the history-shaped
 *   fields above. Two levels because the map holds rule documents: sharing either the map or a
 *   document between two exercises makes a later edit to one reach both, silently, the moment the
 *   progression port makes such an edit possible.
 *
 * `measurementFields` and `revealedColumns` are copied by value for the same reason, though the
 * consequence is milder: both are read-only in practice, and sharing them is a latent aliasing bug
 * rather than an active one.
 *
 * Exported for `draftCopy.ts` and nothing else — a template copy carries every exercise under a new
 * slot with its window intact, which is this function exactly — and deliberately NOT from the
 * slice's barrel.
 */
export function transplantedExercise(exercise: ExerciseDraft): ExerciseDraft {
  return {
    ...exercise,
    slotToken: newSlotToken(),
    activationCycle: null,
    serverCycles: null,
    excludedCycles: [...exercise.excludedCycles],
    progressionRules: cloneProgressionRules(exercise.progressionRules),
    // The unreadable half is copied for the same reason and by a plain spread, because it is a flat
    // map of wire values this build never reaches into: a copy that shared the object would let a
    // later edit to either reach both.
    unreadableProgressionRules: { ...exercise.unreadableProgressionRules },
    measurementFields: [...exercise.measurementFields],
    revealedColumns: [...exercise.revealedColumns],
    progressionOverrides: exercise.progressionOverrides.map((override) => ({ ...override })),
    sets: exercise.sets.map(cloneSet),
    /* THE LIVED RECORD IS DROPPED, and it is the same rule as the fresh `slotToken` above rather
     * than a second one. A new slot has no history: the client was never given this row and never
     * trained it. Carrying the source's entries would put „zrobione 100 × 6" on a cell nobody has
     * ever seen, draw ✓ / ✗ verdicts over a copy, and — once the source is deleted in the same edit
     * — leave the only surviving witness to an occurrence attached to the wrong slot (R3). `null`
     * rather than `[]`: the copy exists on no server, so nothing has answered the question yet. */
    executions: null,
  };
}

/**
 * A COPIED exercise — what `cloneDay` maps over, so `duplicateDay` and `clonePhase` both. The moved
 * row's new slot, **plus a fresh start inside its day's window**.
 *
 * `retirementCycle` and `excludedCycles` are DROPPED here, on the `activationCycle` precedent above
 * it: a copy is a first exposure, so it runs for the whole of its day's window rather than
 * inheriting an end and a set of holes that were answers about the SOURCE's own history. Keeping
 * them would give the trainer a copy that silently sits out weeks they never asked it to.
 *
 * **This is the COPY rule and it is wrong for a MOVE.** `transplantedExercise` is the same new slot
 * with the opposite answer, and the two stay separate rather than becoming one function with a flag,
 * because a flag at the call site is exactly what let a drag reuse the copy's answer.
 */
export function cloneExercise(exercise: ExerciseDraft): ExerciseDraft {
  return { ...transplantedExercise(exercise), retirementCycle: null, excludedCycles: [] };
}

/**
 * A copied day: a **NEW SLOT**, with every exercise beneath it a new slot too.
 *
 * The same rule as `cloneExercise`, one level up, plus the server-owned provenance a copy cannot
 * have: `serverId` and `serverOrder` are both dropped, because a copy has never been stored anywhere.
 * `serverId` is what tells the sequence-recalc preview an existing day from a newly-added one, and
 * `serverOrder` is what tells it a day that MOVED — a copy is neither, and claiming otherwise would
 * make the preview report a reorder the trainer did not perform. `serverCycles` goes with them for
 * the same reason: it is the server's resolution of a row that does not exist. `liveClosedCycles`
 * becomes `[]` rather than `null` — a copy has been trained in no cycle, which is a statement this
 * client can make; `null` would mean "unanswered" and send every gate reading it to the phase
 * fallback, refusing edits to a day that was created a second ago. See `newDay`.
 *
 * **The day's AUTHORED window is KEPT**, and that is the opposite of what its exercises do. A day is
 * copied to repeat a training the trainer built, and the cycles it runs in are part of what they
 * built — a copy that silently reverted to "every cycle of the phase" would put the client back in
 * the weeks the source deliberately sits out. The exercise's window is dropped instead because it is
 * an answer about that exercise's own history inside the day, not about the day's shape.
 */
export function cloneDay(day: DayDraft): DayDraft {
  return {
    ...day,
    slotToken: newSlotToken(),
    serverId: null,
    serverOrder: null,
    excludedCycles: [...day.excludedCycles],
    serverCycles: null,
    liveClosedCycles: [],
    exercises: day.exercises.map(cloneExercise),
  };
}

// ─────────────────────────────────────────────────────────────────────────────────────────────
// PR-B2C — the set table, the exercise list, the day.
//
// Every operation below leaves the tree in a shape the serialiser accepts and the backend will not
// refuse. That is the contract this layer keeps and the components never have to think about:
// set positions stay numbered, overrides stay pointed at the row they describe, a back-off block is
// never left half-built, a superset group never ships with one member, and a day never loses the
// exercise that keeps its phase serialisable.
// ─────────────────────────────────────────────────────────────────────────────────────────────

/**
 * Renumber a set list `1..n`.
 *
 * `setIndex` is not emitted — the wire orders sets by array position — so it is an internal ordinal
 * whose only job is to stay in step with that position. `setSetField` REFUSES when the two
 * disagree, which makes a missed renumber a visible dead edit rather than a silent write to the
 * wrong row, and this is the function that stops them ever disagreeing.
 */
function renumberSets(exercise: Draft<ExerciseDraft>): void {
  exercise.sets.forEach((set, index) => {
    if (set.setIndex !== index + 1) set.setIndex = index + 1;
  });
}

/**
 * Move every per-cycle override at or past `fromSetIndex` (1-based) by `by` rows, and DROP the ones
 * whose row is going away.
 *
 * Overrides address a cell by positional set index, so an insert or a delete that does not carry
 * them along leaves a deload week's pinned weight sitting on a different set than the one the
 * trainer pinned — a silently wrong prescription in a cycle they are not looking at.
 *
 * A negative `by` is a deletion: the row at `fromSetIndex - 1` no longer exists, so a pin ON it has
 * nothing left to describe and is dropped rather than slid onto its neighbour.
 */
function shiftOverrides(exercise: Draft<ExerciseDraft>, fromSetIndex: number, by: number): void {
  exercise.progressionOverrides = exercise.progressionOverrides
    .filter((override) => by > 0 || override.setIndex !== fromSetIndex - 1)
    .map((override) =>
      override.setIndex >= fromSetIndex ? { ...override, setIndex: override.setIndex + by } : override,
    );
}

/**
 * Write a patch onto one set, applying the two rules that must never be skipped: the AMRAP
 * at-most-one-cap resolution, and — when the patch carries a `setType` — a re-normalisation of the
 * load against the exercise's prescription mode.
 *
 * A raw spread leaves the previous role's authored load behind with no cell rendering it: a working
 * set's 60 kg survives the flip to back-off, hidden behind the read-only percent cell that replaced
 * the weight input, and the save is refused over a field nothing on screen showed.
 */
function applySetPatch(
  exercise: Draft<ExerciseDraft>,
  setIndex: number,
  patch: Partial<SetDraft>,
): void {
  const set = exercise.sets[setIndex - 1];
  if (set === undefined) return;
  const resolved = amrapCapExclusivityPatch(set, patch);
  Object.assign(set, resolved);
  if (patch.setType !== undefined) {
    Object.assign(set, normalizeSetLoad({ ...set }, exercise.prescriptionMode));
  }
}

/**
 * Restore the exercise's back-off block after a structural edit, or leave it alone when it is whole.
 *
 * Applied by EVERY set-level operation, which is what makes "the block always heals" a property of
 * this module rather than a rule each surface has to remember. The retype runs through the same
 * field path a hand-made one would, so a promoted descent loses its percentage and gets an
 * authorable value back exactly as if the trainer had retyped it.
 */
function healBlock(exercise: Draft<ExerciseDraft>): void {
  const heal = backoffBlockHeal(exercise.sets);
  if (heal === null) return;
  const target = exercise.sets[heal.index];
  if (target === undefined) return;
  applySetPatch(exercise, heal.index + 1, setTypePatch({ ...target }, heal.setType, exercise.sets));
}

/**
 * Normalise a day's superset groups so none ships malformed.
 *
 * · A group down to ONE surviving member loses its group id. A one-member group is not a superset,
 *   and a lone letter left behind after its partner was deleted is not a pairing.
 * · A group whose survivors are WARM-UPS ONLY has those warm-ups dropped: a dedicated warm-up must
 *   never outlive the work exercise it was bound to.
 *
 * Idempotent, and applied after every removal and every role change.
 */
function normaliseGroups(day: Draft<DayDraft>): void {
  const workByGroup = new Map<string, number>();
  for (const exercise of day.exercises) {
    if (!exercise.group || isWarmupRole(exercise.role)) continue;
    workByGroup.set(exercise.group, (workByGroup.get(exercise.group) ?? 0) + 1);
  }

  day.exercises = day.exercises.filter(
    (exercise) =>
      !exercise.group || !isWarmupRole(exercise.role) || (workByGroup.get(exercise.group) ?? 0) > 0,
  );

  dissolveIncompleteGroups(day);
}

/**
 * Clear every bracket that is no longer a pairing, and DELETE nothing.
 *
 * A group is incomplete when it holds fewer than two exercises, or when nothing left in it is work
 * — a lone letter is not a pairing, and a bracket of warm-ups is not a mini-group.
 *
 * It is `normaliseGroups`' second half, extracted so `groupAsSuperset` can reach it without the
 * first. That half DROPS an orphaned warm-up, on the rule that "a dedicated warm-up must never
 * outlive the work exercise it was bound to" — right after a removal, and wrong after a join: there
 * the lift did not die, it moved one list position into another bracket, and taking its warm-up
 * with it would delete an exercise the trainer never asked to lose. Unbracketed, that warm-up joins
 * the day's own warm-up run, which is where an unattached warm-up already lives.
 */
function dissolveIncompleteGroups(day: Draft<DayDraft>): void {
  const total = new Map<string, number>();
  const work = new Map<string, number>();
  for (const exercise of day.exercises) {
    if (!exercise.group) continue;
    total.set(exercise.group, (total.get(exercise.group) ?? 0) + 1);
    if (!isWarmupRole(exercise.role)) {
      work.set(exercise.group, (work.get(exercise.group) ?? 0) + 1);
    }
  }
  for (const exercise of day.exercises) {
    if (!exercise.group) continue;
    if ((total.get(exercise.group) ?? 0) <= 1 || (work.get(exercise.group) ?? 0) === 0) {
      exercise.group = null;
    }
  }
}

/**
 * Re-derive the day's rest columns (D3 ∘ D12) after anything that moves, adds or retypes an exercise.
 *
 * The result is written back FIELD BY FIELD rather than by replacing the array. `deriveRestColumns`
 * is pure and returns fresh objects, and assigning those into the Immer draft would record a
 * whole-array `replace` patch on every keystroke that touched a rest cell — a snapshot with extra
 * steps, which is the exact cost patch-based undo exists to avoid.
 */
function refreshRestColumns(day: Draft<DayDraft>): void {
  const resolved = deriveRestColumns(day.exercises, dayRestSeconds(day.restSeconds));
  resolved.forEach((next, index) => {
    const current = day.exercises[index];
    if (current !== undefined && current.showsRestColumn !== next.showsRestColumn) {
      current.showsRestColumn = next.showsRestColumn;
    }
  });
}

// ── Sets ─────────────────────────────────────────────────────────────────────────────────────

/**
 * Change one set's TYPE — the fields, then the block repair.
 *
 * A **"Back-off" pick does not come through here**: a descent is a percentage OF something, so the
 * top has to be resolved first. That is `makeBackoffSet`, which adds or binds a row. This is the
 * relabel, and it is what the badge cycles through.
 */
export function changeSetType(
  draft: MutablePlan,
  address: SetAddress,
  nextType: string,
): void {
  const site = findExerciseSite(draft, address.exerciseSlotToken);
  if (site === null) return;
  const target = site.exercise.sets[address.setIndex - 1];
  if (target === undefined) return;
  if (nextType === TOP_SET_TYPE) demoteExistingTop(site.exercise, address.setIndex);
  applySetPatch(site.exercise, address.setIndex, setTypePatch({ ...target }, nextType, site.exercise.sets));
  healBlock(site.exercise);
  refreshRestColumns(site.day);
}

/**
 * Promoting a set to `Top` DEMOTES the one already there.
 *
 * **A deliberate divergence from the mobile port, and the one place this app is stricter than the
 * phone.** Mobile's heal repairs a top leading nothing and descents leading nowhere, but it does
 * not stop a second `Top` — `resolveTopIndex` simply takes the first, and the shape survives.
 *
 * The backend does not: `Domain/Progression/SetShapeValidator.RequireSingleTop` refuses an exercise
 * carrying back-off sets and more than one `Top` outright — "one back-off block per exercise, so
 * exactly one Top set is allowed". So the phone can author a ladder it cannot save, and a trainer
 * who promoted a second top here would meet a refusal naming a rule nothing on screen expressed.
 * Demoting is the reading that keeps the invariant true by construction; the demoted row becomes an
 * ordinary working set, which is what it was before it led anything.
 */
function demoteExistingTop(exercise: Draft<ExerciseDraft>, exceptSetIndex: number): void {
  exercise.sets.forEach((set, index) => {
    if (index + 1 === exceptSetIndex || !isTopSet(set)) return;
    applySetPatch(exercise, index + 1, setTypePatch({ ...set }, WORKING_SET_TYPE, exercise.sets));
  });
}

/**
 * Give the exercise one more back-off descent — the whole "the trainer picked Back-off" act.
 *
 * `topSetIndex` is the trainer's answer to `bindableTopSets`, and there are four shapes:
 *
 *  · **the picked row itself** → it becomes the `Top` in place, keeping its position and its
 *    authored prescription, and a fresh descent lands directly below. This is the whole answer on a
 *    single-set exercise, and it is the one that makes the pick non-destructive — the load a descent
 *    may not carry never has to be discarded, because the row holding it never becomes a descent.
 *  · **another row** → that row becomes the `Top`, the picked row becomes the descent, and nothing
 *    is inserted, so nothing addressed by position moves.
 *  · **`null` with no block yet** → a fresh `Top` is inserted directly ABOVE the picked row. A new
 *    top means a new row: nothing the trainer authored is repurposed.
 *  · **`null` with a block already** → the block simply gains another descent.
 *
 * Picking Back-off on the block's own TOP is none of the four and is a no-op. The row is already
 * what the pick would make it lead: falling through would strip its authored weight, the heal would
 * promote it straight back, and the trainer would be left with an action that looks like it did
 * nothing except delete their 100 kg.
 */
export function makeBackoffSet(
  draft: MutablePlan,
  address: SetAddress,
  topSetIndex: number | null,
): void {
  const site = findExerciseSite(draft, address.exerciseSlotToken);
  if (site === null) return;
  const exercise = site.exercise;
  const picked = exercise.sets[address.setIndex - 1];
  if (picked === undefined || isTopSet(picked)) return;

  const { startsBlock, percent } = backoffPick(exercise.sets, picked);
  const mode = exercise.prescriptionMode;

  if (topSetIndex === address.setIndex) {
    // The picked row leads its own block; the DESCENT is the row that gets created, copying the
    // top's shape (reps, rest, effort) at the chosen fraction.
    const descent = normalizeSetLoad(
      { ...picked, localId: newSetLocalId(), setType: 'Backoff', prescribedPercent: percent, note: null },
      mode,
    );
    applySetPatch(exercise, address.setIndex, setTypePatch({ ...picked }, TOP_SET_TYPE, exercise.sets));
    exercise.sets.splice(address.setIndex, 0, descent);
    // The picked row and everything above it stay put; a pin below the new descent must follow the
    // row it describes.
    shiftOverrides(exercise, address.setIndex + 1, 1);
    renumberSets(exercise);
    healBlock(exercise);
    refreshRestColumns(site.day);
    return;
  }

  applySetPatch(exercise, address.setIndex, setTypePatch({ ...picked }, 'Backoff', exercise.sets));

  if (topSetIndex === null && startsBlock) {
    // A genuinely NEW top: it carries only its neighbour's shape and none of its load, because the
    // trainer asked for a new top rather than for one of their rows to be repurposed into one.
    const freshTop = normalizeSetLoad(
      {
        ...picked,
        localId: newSetLocalId(),
        setType: TOP_SET_TYPE,
        prescribedWeight: null,
        prescribedPercent: null,
        note: null,
      },
      mode,
    );
    exercise.sets.splice(address.setIndex - 1, 0, freshTop);
    // The top goes ABOVE the picked row, so the picked row's own pin moves with it.
    shiftOverrides(exercise, address.setIndex, 1);
  } else if (topSetIndex !== null) {
    const bound = exercise.sets[topSetIndex - 1];
    if (bound !== undefined) {
      demoteExistingTop(exercise, topSetIndex);
      applySetPatch(exercise, topSetIndex, setTypePatch({ ...bound }, TOP_SET_TYPE, exercise.sets));
    }
  }

  renumberSets(exercise);
  healBlock(exercise);
  refreshRestColumns(site.day);
}

/**
 * Insert a set directly below `afterSetIndex`, copying that row's prescription — the canon's
 * "+ Seria", which copies the previous set rather than opening a blank one.
 *
 * **A copy of a TOP is an ordinary working set.** `Top` is the block's single reference role rather
 * than a style that repeats, and duplicating one would give the exercise two — the one shape the
 * heal cannot express as a single retype, so it is refused at the only place that could produce it.
 *
 * The per-set NOTE is deliberately not copied. It is a remark about one set; carrying it onto a
 * duplicate would put words in the trainer's mouth about a row they have not looked at yet.
 *
 * `afterSetIndex` past the end appends, which is what the "+ Seria" button passes.
 */
export function insertSetBelow(
  draft: MutablePlan,
  exerciseSlotToken: string,
  afterSetIndex: number,
): void {
  const site = findExerciseSite(draft, exerciseSlotToken);
  if (site === null) return;
  const exercise = site.exercise;
  if (exercise.sets.length >= COLLECTION_CAPS.setsPerExercise.max) return;

  const at = Math.min(Math.max(afterSetIndex, 0), exercise.sets.length);
  // The row ABOVE, which is what "insert below" copies; at the very top there is none, so the row
  // being pushed down is the nearest thing to "the previous prescription".
  const source = exercise.sets[at - 1] ?? exercise.sets[at];
  const inserted: SetDraft =
    source === undefined
      ? createSet(at + 1, isWarmupRole(exercise.role))
      : {
          ...source,
          // A fresh row, not the copied one: the inserted set is new on screen even though every
          // value it carries came from its neighbour.
          localId: newSetLocalId(),
          setIndex: at + 1,
          setType: isTopSet(source) ? WORKING_SET_TYPE : source.setType,
          note: null,
        };

  exercise.sets.splice(at, 0, inserted);
  shiftOverrides(exercise, at + 1, 1);
  renumberSets(exercise);
  healBlock(exercise);
  refreshRestColumns(site.day);
}

/**
 * Remove one set.
 *
 * **An exercise always keeps at least one set.** `PostExerciseTemplateInput.Sets` is `[MinLength(1)]`,
 * so a setless exercise is a save the backend refuses over a row the trainer can no longer see. The
 * operation returns quietly rather than refusing loudly, because the surface disables the control
 * and states the reason — see `canRemoveSet`, which is the same question asked where an answer can
 * be shown.
 */
export function removeSet(draft: MutablePlan, address: SetAddress): void {
  const site = findExerciseSite(draft, address.exerciseSlotToken);
  if (site === null) return;
  const exercise = site.exercise;
  if (exercise.sets.length <= 1) return;
  if (exercise.sets[address.setIndex - 1] === undefined) return;

  exercise.sets.splice(address.setIndex - 1, 1);
  shiftOverrides(exercise, address.setIndex, -1);
  renumberSets(exercise);
  healBlock(exercise);
  refreshRestColumns(site.day);
}

/** Whether removing a set is offered. The last one is not — see `removeSet`. */
export function canRemoveSet(exercise: Pick<ExerciseDraft, 'sets'>): boolean {
  return exercise.sets.length > 1;
}

// ── Exercises ────────────────────────────────────────────────────────────────────────────────

/**
 * Add an exercise to a day, from a library pick.
 *
 * A work exercise lands at the END of the day. A **warm-up-role** exercise lands at the end of the
 * leading warm-up run, just before the first work exercise — the canon renders a session as two
 * named blocks and the wire's `Order` follows array position, so the ordering invariant
 * `[…warm-ups, …work]` is what makes "top" survive a save and reload.
 *
 * The new exercise's sets are stamped with the DAY's rest, so a fresh pick on a 120-second day
 * never keeps the constructor's flat 90 and never reloads with a rest column it did not ask for.
 *
 * **It returns the new exercise's slot token**, or `null` when nothing was added — the day was not
 * found, or it is already at the cap. A create that does not name what it created leaves its caller
 * guessing at a position, and the one caller that has to know is the library: the drawer it was
 * pressed in closes over the day, so the card that was just made is where focus has to land.
 */
export function addExercise(
  draft: MutablePlan,
  daySlotToken: string,
  pick: ExercisePick,
  role: AuthorableExerciseRole = 'Main',
): string | null {
  const day = findDay(draft, daySlotToken);
  if (day === null) return null;
  if (day.exercises.length >= COLLECTION_CAPS.exercisesPerSession.max) return null;

  const rest = dayRestSeconds(day.restSeconds);
  // Cast at the seam where a freshly CONSTRUCTED node joins the draft. The factories return the
  // app's own read-only shape; Immer's `Draft<T>` is the same shape with its arrays made writable,
  // and a plain object about to be adopted by the producer is exactly what that type describes.
  // Nothing is widened and no invariant is bypassed — the alternative is making the model's types
  // mutable everywhere so that one insertion type-checks.
  const exercise = restampExerciseRest(createExercise(pick, role), rest) as Draft<ExerciseDraft>;

  if (role === 'Warmup') {
    let at = 0;
    while (at < day.exercises.length && isStandaloneDayWarmup(day.exercises[at])) at += 1;
    day.exercises.splice(at, 0, exercise);
  } else {
    day.exercises.push(exercise);
  }
  refreshRestColumns(day);
  return exercise.slotToken;
}

/** What a BATCH add did, or why it did nothing. See `addExercises`. */
export type LibraryBatchInsert =
  | { readonly kind: 'refused'; readonly reason: DropRefusal }
  | { readonly kind: 'inserted'; readonly exerciseSlotTokens: readonly string[] };

/**
 * Add SEVERAL library picks to one day, in the order they were chosen — the library's "Dodaj N
 * ćwiczeń" (owner, 16 September 2026).
 *
 * ── It is one act, and that is the whole reason it is one operation ──────────────────────────
 * The store runs it inside a single `runOperation`, so five movements arrive as one patch, one
 * dirty flag and **one undo entry**: the trainer who picked five and changed their mind presses
 * Cmd+Z once, not five times. A component looping over `addExercise` would have produced five of
 * everything, and it would have had no way to refuse the batch as a whole.
 *
 * ── A refused target refuses the WHOLE batch ────────────────────────────────────────────────
 * The gate is asked once, with the arriving count, before anything is written — so a selection that
 * would overrun the day's exercise cap inserts nothing rather than filling the day to the cap and
 * silently dropping the rest. `addExercise`'s own cap check below it is then unreachable, and stays
 * where it is: it guards the single path, which has its own callers.
 *
 * **Every pick mints a fresh slot token**, because each one goes through `addExercise` and therefore
 * through `createExercise` — the same identity a single press mints, N times.
 *
 * An empty selection is refused rather than answered `inserted` with nothing in it: there is no act
 * to record, and a caller would otherwise announce that nothing had been added.
 */
export function addExercises(
  draft: MutablePlan,
  daySlotToken: string,
  picks: readonly ExercisePick[],
  role: AuthorableExerciseRole = 'Main',
): LibraryBatchInsert {
  if (picks.length === 0) return { kind: 'refused', reason: 'unknownTarget' };
  const refusal = libraryDropRefusal(draft, daySlotToken, picks.length);
  if (refusal !== null) return { kind: 'refused', reason: refusal };

  const tokens: string[] = [];
  for (const pick of picks) {
    const token = addExercise(draft, daySlotToken, pick, role);
    // Unreachable behind the gate above — it answers `null` only for a day that is not there or a
    // day with no room, and the gate has just asked both for the whole batch. It is an answer
    // rather than a `!` because a gate that stops agreeing with the operation must not be able to
    // report an insert that did not happen.
    if (token === null) return { kind: 'refused', reason: 'unknownTarget' };
    tokens.push(token);
  }
  return { kind: 'inserted', exerciseSlotTokens: tokens };
}

/** A day-level warm-up: the warm-up role with NO group. A grouped warm-up is bound to one exercise. */
function isStandaloneDayWarmup(exercise: Pick<ExerciseDraft, 'role' | 'group'> | undefined): boolean {
  return exercise !== undefined && isWarmupRole(exercise.role) && !exercise.group;
}

/**
 * Can this exercise be removed?
 *
 * **The guard is phase-scoped, not day-scoped.** `draftToRequest` DROPS a day carrying no exercise
 * and then REFUSES the whole plan if that leaves a phase with no day at all — because omitting the
 * collection is not "leave the days alone", it is a `200` over the days the trainer just deleted.
 * So the exercise that may not go is the last one in the last day of its phase that still carries
 * any: removing it makes the entire plan unsavable, in a phase the trainer may not even be viewing.
 *
 * Returned as a reason rather than a boolean, so the surface can say WHY the control is disabled.
 * A disabled control carrying an explanation stays focusable, or it drops out of the tab order and
 * takes the reason with it.
 */
export type ExerciseRemovalRefusal = 'lastExerciseInPhase';

export function exerciseRemovalRefusal(
  draft: PlanDraft,
  exerciseSlotToken: string,
): ExerciseRemovalRefusal | null {
  for (const phase of draft.phases) {
    for (const day of phase.days) {
      if (!day.exercises.some((exercise) => exercise.slotToken === exerciseSlotToken)) continue;
      if (day.exercises.length > 1) return null;
      // This removal empties the day. Does any OTHER day in the phase still carry an exercise?
      const survives = phase.days.some(
        (other) => other.slotToken !== day.slotToken && other.exercises.length > 0,
      );
      return survives ? null : 'lastExerciseInPhase';
    }
  }
  return null;
}

/**
 * Remove an exercise from its day.
 *
 * Guarded by `exerciseRemovalRefusal` — see it for why the rule is phase-scoped. Afterwards the
 * day's groups are normalised (a one-member superset is not a superset; an orphaned bound warm-up
 * does not outlive its work exercise) and the rest columns are re-derived, because a deletion can
 * change which member closes a superset round.
 */
export function removeExercise(draft: MutablePlan, exerciseSlotToken: string): void {
  if (exerciseRemovalRefusal(draft, exerciseSlotToken) !== null) return;
  const site = findExerciseSite(draft, exerciseSlotToken);
  if (site === null) return;
  site.day.exercises.splice(site.index, 1);
  normaliseGroups(site.day);
  refreshRestColumns(site.day);
}

/**
 * Move an exercise into or out of the day's warm-up block.
 *
 * A role change, and nothing else — the exercise keeps its slot token, its sets, its prescription
 * and its progression rule. What changes is which block it renders in and whether it is counted:
 * a warm-up-role exercise contributes neither a counted set nor a muscle point.
 *
 * The exercise is also REPOSITIONED to keep `[…warm-ups, …work]` intact, because `Order` follows
 * array position on the wire and a warm-up stranded below the work would reload below it.
 *
 * A role change into `Warmup` drops the group: a warm-up inside a work-only superset would bracket
 * itself into a shape that is neither a superset nor a mini-group.
 */
export function setExerciseRole(
  draft: MutablePlan,
  exerciseSlotToken: string,
  role: AuthorableExerciseRole,
): void {
  const site = findExerciseSite(draft, exerciseSlotToken);
  if (site === null || site.exercise.role === role) return;
  const { day, index } = site;

  const [moved] = day.exercises.splice(index, 1);
  if (moved === undefined) return;
  moved.role = role;
  if (role === 'Warmup') moved.group = null;

  if (role === 'Warmup') {
    let at = 0;
    while (at < day.exercises.length && isStandaloneDayWarmup(day.exercises[at])) at += 1;
    day.exercises.splice(at, 0, moved);
  } else {
    day.exercises.push(moved);
  }
  normaliseGroups(day);
  refreshRestColumns(day);
}

/** The trainer's coaching tip on one exercise. Real, server-stored, shown to the athlete. */
export function setExerciseNotes(
  draft: MutablePlan,
  exerciseSlotToken: string,
  notes: string,
): void {
  const exercise = findExercise(draft, exerciseSlotToken);
  if (exercise === null) return;
  const trimmed = notes.trim();
  exercise.notes = trimmed.length > 0 ? trimmed : null;
}

/**
 * Switch an exercise between absolute kilograms and %-of-1RM.
 *
 * Every set is re-normalised, which is what makes the switch honest: the mode decides which of the
 * two overloaded load cells a row may carry, so leaving the other populated would hide an authored
 * value behind the cell that replaced it until the save was refused over it. Ladder members keep
 * their percent in both modes — it is their construction, not a load prescription.
 */
export function setPrescriptionMode(
  draft: MutablePlan,
  exerciseSlotToken: string,
  mode: string,
): void {
  const exercise = findExercise(draft, exerciseSlotToken);
  if (exercise === null || exercise.prescriptionMode === mode) return;
  exercise.prescriptionMode = mode;
  exercise.sets.forEach((set, index) => {
    Object.assign(exercise.sets[index] as SetDraft, normalizeSetLoad({ ...set }, mode));
  });
}

// ── Supersets ────────────────────────────────────────────────────────────────────────────────

/**
 * Link an exercise with the one FOLLOWING it into a superset.
 *
 * "With the next" rather than a multi-select, because the day list is one vertical column and
 * adjacency is what a superset means: `weekProjection` brackets CONTIGUOUS exercises sharing a
 * group, so two separated members carrying the same letter are not performed together and a rail
 * drawn across the exercise between them would be a lie.
 *
 * Refused — silently, with the surface stating the reason — when either row is a warm-up, when
 * either already belongs to a group, or when there is no next exercise. A warm-up is never pulled
 * into a superset: a group carrying one renders as a mini-group, which is a different concept.
 */
export function groupWithNext(draft: MutablePlan, exerciseSlotToken: string): void {
  if (!canGroupWithNext(draft, exerciseSlotToken)) return;
  const site = findExerciseSite(draft, exerciseSlotToken);
  if (site === null) return;
  const next = site.day.exercises[site.index + 1];
  if (next === undefined) return;
  const group = nextGroupId(site.day);
  site.exercise.group = group;
  next.group = group;
  refreshRestColumns(site.day);
}

/**
 * The bracket id a new superset takes: the first letter of the alphabet the day is not already
 * using, which is exactly what the canon prints — "2a / 2b", "4a / 4b".
 *
 * **A minted slot token will not do here, and that is not a style preference.** `Group` is
 * `varchar(16)` on the backend (`TEXT_LIMITS.exerciseGroup`) and a token is 22 characters, so a
 * token-as-group is a save the backend refuses over a field the trainer never typed into. Found by
 * `aSupersetBracketAuthoredInTheBrowserShipsAsAGroupOnBothMembers` rather than by reading the
 * column, which is why that assertion checks the LENGTH and not merely the value.
 *
 * A day cannot exhaust the alphabet — `exercisesPerSession` caps at 20, so at most ten brackets —
 * but the fallback is a truncated token rather than a collision, because reusing a letter already
 * in the day would fuse two unrelated pairs the moment they became adjacent.
 */
function nextGroupId(day: Draft<DayDraft>): string {
  const taken = new Set(day.exercises.map((exercise) => exercise.group).filter(Boolean));
  for (let code = 0; code < 26; code += 1) {
    const letter = String.fromCharCode(65 + code);
    if (!taken.has(letter)) return letter;
  }
  return newSlotToken().slice(0, TEXT_LIMITS.exerciseGroup.max);
}

/** Whether "link with the next" is offered. Asked where the answer can be shown, and by the operation. */
export function canGroupWithNext(draft: PlanDraft, exerciseSlotToken: string): boolean {
  for (const phase of draft.phases) {
    for (const day of phase.days) {
      const index = day.exercises.findIndex((exercise) => exercise.slotToken === exerciseSlotToken);
      if (index === -1) continue;
      const exercise = day.exercises[index];
      const next = day.exercises[index + 1];
      if (exercise === undefined || next === undefined) return false;
      if (isWarmupRole(exercise.role) || isWarmupRole(next.role)) return false;
      // Truthy, not `!= null`: `group: ""` is ungrouped, because the backend's column has no
      // non-empty validator and two empty-grouped neighbours are two singles, not a pair.
      return !exercise.group && !next.group;
    }
  }
  return false;
}

/** What a join actually did — `null` when it did nothing. See `groupAsSuperset`. */
export interface SupersetJoin {
  /** The fresh bracket letter every member now carries. */
  readonly group: string;
  /** The members that were joined, in the order the day now holds them. */
  readonly memberSlotTokens: readonly string[];
}

/**
 * Join any two or more SELECTED exercises of one day into a single superset, pulling them together.
 *
 * `groupWithNext` links neighbours and stays exactly as it is; this is the second, wider act the
 * owner asked for on 7 September 2026, and it closes a gap rather than opening one — the mobile
 * builder has authored supersets this way since `groupAsSuperset` (`planDraftHelpers.ts`), and its
 * semantics are mirrored here so the two clients cannot disagree about what a join did.
 *
 * ── Contiguity is the invariant, so the members MOVE ─────────────────────────────────────────
 * `weekProjection` brackets a CONSECUTIVE run sharing a group, on both platforms. Two separated
 * members carrying one letter would draw a rail across the exercise standing between them and claim
 * a pairing that is not performed. So the members are spliced out and reinserted as one run at the
 * anchor — the position of the LOWEST-indexed member, measured among the exercises that stay — and
 * the topmost selected card is where the block ends up, which is where the eye left it.
 *
 * Renumbering positions this way is safe and is what both clients already do: a not-started workout
 * is matched by `SlotToken` while it stays in its phase (`Services/WorkoutExecutionPropagator.cs`).
 * It is PHASE order that must stay contiguous, and nothing here touches a phase.
 *
 * ── Who is eligible, and why fewer than two is a no-op ───────────────────────────────────────
 * A member must be in this day and must not be a warm-up: a group carrying one renders as a
 * mini-group, which is a different concept. One survivor is not a superset, so the whole call does
 * nothing rather than minting a group of one.
 *
 * **An exercise ALREADY IN A BRACKET is eligible, and the join absorbs it (owner, 7 September
 * 2026).** It was ineligible for one round, so ticking a member of an existing `1a`/`1b` pair
 * beside a standalone card minted a second bracket next to the first; the mock absorbs instead and
 * produces one three-member block, `1a` `1b` `1c`. Every other rule is unchanged — one fresh
 * letter, spliced contiguous at the lowest picked index, clamped after the day's leading warm-up
 * run.
 *
 * Two consequences of absorbing, both handled by `dissolveIncompleteGroups` rather than left to a
 * caller: a bracket the picks emptied down to ONE exercise stops being a bracket, and a mini-group
 * whose lift was absorbed leaves its warm-up unbracketed rather than deleted — the lift moved one
 * list position, it did not die, so `normaliseGroups`' "a dedicated warm-up must never outlive its
 * lift" rule does not apply.
 *
 * **This diverges from the phone in the GESTURE, not in the data.** Mobile's own `groupAsSuperset`
 * refuses a grouped pick, so it cannot author a three-member superset this way. What this produces
 * is an ordinary group of three sharing one `group` value — a legal wire state the phone reads and
 * renders as a three-exercise superset like any other. Nothing about the tree becomes unreadable
 * there; mobile simply has no gesture that reaches this shape yet.
 *
 * The anchor is clamped to after the day's leading warm-up run. Members are work-role, so the
 * minimum index can never fall inside that prefix — the clamp is a guard, not a behaviour, and it
 * keeps warm-ups-first true whatever a future caller passes.
 *
 * ── It answers with what it DID, which `addExercise` already does ────────────────────────────
 * `null` when nothing was joined — no such day, or fewer than two eligible members
 * after the filter. The caller announces on the answer rather than on the press: a bar reading
 * "Wybrano 2" can be one deletion behind the tree, and "Połączono w superset: 2" over an unchanged
 * plan is the worst kind of announcement. The recipe runs synchronously inside `setState`, so the
 * answer is available before the store's call returns.
 */
export function groupAsSuperset(
  draft: MutablePlan,
  daySlotToken: string,
  exerciseSlotTokens: readonly string[],
): SupersetJoin | null {
  const site = findDaySite(draft, daySlotToken);
  if (site === null) return null;
  const day = site.day;

  const selected = new Set(exerciseSlotTokens);
  // Filtered off the day's own array, so the survivors arrive in list order and the first of them
  // is the anchor without a second sort.
  const members = day.exercises.filter(
    (exercise) => selected.has(exercise.slotToken) && !isWarmupRole(exercise.role),
  );
  if (members.length < 2) return null;

  const group = nextGroupId(day);
  const memberTokens = new Set(members.map((exercise) => exercise.slotToken));
  const anchor = members[0];
  if (anchor === undefined) return null;

  const rest = day.exercises.filter((exercise) => !memberTokens.has(exercise.slotToken));
  const anchorIndex = day.exercises.findIndex(
    (exercise) => exercise.slotToken === anchor.slotToken,
  );
  let at = day.exercises
    .slice(0, anchorIndex)
    .filter((exercise) => !memberTokens.has(exercise.slotToken)).length;

  let warmupPrefix = 0;
  while (warmupPrefix < rest.length && isStandaloneDayWarmup(rest[warmupPrefix])) warmupPrefix += 1;
  if (at < warmupPrefix) at = warmupPrefix;

  for (const member of members) member.group = group;
  rest.splice(at, 0, ...members);
  day.exercises = rest;
  // A bracket the picks took some of can be left holding one exercise, or holding only the warm-up
  // that was bound to the lift that just left. Neither is a pairing, and neither is a deletion.
  dissolveIncompleteGroups(day);
  refreshRestColumns(day);

  return { group, memberSlotTokens: members.map((member) => member.slotToken) };
}

/**
 * Pull one exercise out of its superset.
 *
 * If that leaves exactly one other member behind, the orphan clears its group too — a one-member
 * group is not a superset, and a letter with nothing to pair with is not a pairing.
 */
export function unlinkSuperset(draft: MutablePlan, exerciseSlotToken: string): void {
  const site = findExerciseSite(draft, exerciseSlotToken);
  if (site === null || !site.exercise.group) return;
  site.exercise.group = null;
  normaliseGroups(site.day);
  refreshRestColumns(site.day);
}

/**
 * What an attempt to give one exercise its OWN warm-up did.
 *
 * A refusal rather than a silent `null`, and the vocabulary is `DropRefusal` rather than a second
 * one of its own: the two ways into the library — the drop and the press — already word every
 * refusal out of that enum, and a chip that failed for the day's exercise cap has to say the same
 * sentence the drop says.
 */
export type WarmupInsert =
  | { readonly kind: 'refused'; readonly reason: DropRefusal }
  | { readonly kind: 'inserted'; readonly exerciseSlotToken: string };

/**
 * Give ONE exercise its own warm-up — the mock's `Rozgrzewka` chip, and the mini-group behind it.
 *
 * ── A mini-group is not a superset, and the difference is the ROLE inside the bracket ────────
 * Both are a `group` letter shared by adjacent exercises. A bracket whose members are all work is a
 * superset — performed alternating, and `weekProjection` draws it as one. A bracket holding a
 * `Warmup` and the lift it precedes is a MINI-GROUP: the warm-up belongs to that movement rather
 * than to the day, so it is not counted, it is not moved with the day's own warm-up run, and it dies
 * with its lift. `normaliseGroups` already words that last rule — "a dedicated warm-up must never
 * outlive the work exercise it was bound to" — and it has been enforcing it on every removal since
 * long before anything on the web could author one.
 *
 * The phone has had this since it shipped (`planDraftHelpers.insertWarmupGroup`, reached by
 * `{ type: 'addWarmup' }`), and this is that operation's web twin, rule for rule:
 *
 * · **One fresh group id**, taken from `nextGroupId` rather than from a minted token — `Group` is
 *   `varchar(16)` on the backend and a slot token is 22 characters. The phone can use its temp id
 *   because it re-keys on save; this tree emits the letter it holds.
 * · **The warm-up goes immediately BEFORE its lift**, because the bracket is contiguous or it is not
 *   a bracket: `weekProjection` groups only ADJACENT members sharing a letter, and `Order` follows
 *   array position on the wire.
 * · **One dedicated warm-up per exercise.** Asking twice REPLACES rather than stacking a second one
 *   into the bracket — the chip is "give this movement its warm-up", not "add another".
 * · **A work-only superset SPLITS.** A warm-up dropped into a two-lift bracket would make it neither
 *   a superset nor a mini-group, so the target leaves under a fresh letter with its warm-up, and the
 *   partner it left behind loses a bracket that is no longer a pairing (`dissolveIncompleteGroups`,
 *   which clears letters and deletes nothing — the partner is a lift the trainer still wants).
 *
 * A warm-up cannot itself be given one, and neither can an exercise the tree no longer holds: both
 * answer `unknownTarget`, which is what the drop says about an address that has gone.
 */
export function insertWarmupFor(
  draft: MutablePlan,
  targetExerciseSlotToken: string,
  pick: ExercisePick,
): WarmupInsert {
  const site = findExerciseSite(draft, targetExerciseSlotToken);
  if (site === null || isWarmupRole(site.exercise.role)) {
    return { kind: 'refused', reason: 'unknownTarget' };
  }
  const { day, exercise, index } = site;

  // The same gate the press and the drop ask, so the day's exercise cap is one rule with one
  // sentence rather than three copies that can drift.
  const refusal = libraryDropRefusal(draft, day.slotToken);
  if (refusal !== null) return { kind: 'refused', reason: refusal };

  const current = exercise.group;
  // A bracket the target already sits in is REUSED only where a warm-up may live in it — that is,
  // where it is not a pairing of two lifts. Anything else splits.
  const isWorkOnlySuperset =
    Boolean(current) &&
    day.exercises.filter((other) => other.group === current && !isWarmupRole(other.role)).length >= 2;
  const reuse = Boolean(current) && !isWorkOnlySuperset;

  const group = reuse && current !== null ? current : nextGroupId(day);
  const warmup = restampExerciseRest(
    createExercise(pick, 'Warmup', group),
    dayRestSeconds(day.restSeconds),
    // Cast at the seam where a freshly CONSTRUCTED node joins the draft — the same one and the same
    // reason as `addExercise`'s.
  ) as Draft<ExerciseDraft>;

  if (reuse) {
    const existing = day.exercises.findIndex(
      (other) => other.group === current && isWarmupRole(other.role),
    );
    if (existing !== -1) {
      day.exercises.splice(existing, 1, warmup);
      refreshRestColumns(day);
      return { kind: 'inserted', exerciseSlotToken: warmup.slotToken };
    }
  } else {
    exercise.group = group;
  }

  day.exercises.splice(index, 0, warmup);
  // Only where the target LEFT a bracket: the one it left may now hold a single lift, whose letter
  // is not a pairing. Reusing a bracket disturbs nothing, and running this over the mini-group that
  // was just built would be asking a question already answered.
  if (!reuse) dissolveIncompleteGroups(day);
  refreshRestColumns(day);
  return { kind: 'inserted', exerciseSlotToken: warmup.slotToken };
}

// ── The day ──────────────────────────────────────────────────────────────────────────────────

/**
 * Create a training day — the canon's "+ Trening" on an empty weekday column, and the tape's
 * "+ Trening" tile after the last rest.
 *
 * The day is minted with a **fresh slot token** and no server identity, so the backend reads it as
 * an addition rather than as an edit to a day it already knows, and the structural preview reads it
 * as newly added rather than as a day the trainer moved.
 *
 * It arrives with no exercises. That is deliberate and it is why this is safe: `draftToRequest`
 * DROPS an exercise-less day, so an empty new day changes nothing on the wire and cannot make a
 * save fail — the trainer adds an exercise, and only then does the day exist as far as the server
 * is concerned. It is also why `pendingStructuralEdits` only counts a new day once it carries one.
 *
 * ── `dayOfWeek` is `null` on a SEQUENCE plan, and that is the whole difference ────────────────
 * A sequence day has no weekday to be placed on; it is APPENDED, because a sequence phase is an
 * ordered list and the new day joins its end. `createDay` seeds it with `restDaysAfter: 1` and
 * `dayOfWeek: null`, which is mobile's own seed (`draftFactory`) and what keeps the day
 * serialisable for its mode. A weekday passed on a sequence plan is ignored rather than refused —
 * `createDay` drops it for the same reason.
 *
 * A weekday that already has a day is a no-op on a DATE plan: the canon's affordance only appears
 * on an empty column, and two days on one weekday is a shape this app does not author. There is no
 * counterpart on a sequence plan, where two days may sit next to each other with no gap at all.
 *
 * Guarded by `dayAdditionRefusal` — the cap and a phase closed to new days — so every affordance
 * that adds a day inherits both.
 */
export function addDay(draft: MutablePlan, phaseIndex: number, dayOfWeek: number | null): void {
  const phase = draft.phases[phaseIndex];
  if (phase === undefined) return;
  if (dayAdditionRefusal(draft, phaseIndex) !== null) return;
  if (draft.mode !== 'date') {
    // Cast at the construction seam — see `addExercise`.
    phase.days.push(createDay(draft.mode, null) as Draft<DayDraft>);
    return;
  }
  if (dayOfWeek === null) return;
  if (phase.days.some((day) => day.dayOfWeek === dayOfWeek)) return;

  const day = createDay(draft.mode, dayOfWeek) as Draft<DayDraft>;
  // Inserted in weekday order rather than appended, so the array's order and the column order the
  // trainer is looking at agree. `Order` is assigned from array position at serialisation, and the
  // backend matches a not-started workout by the day's SlotToken, so this is presentation — but a
  // list whose order disagrees with the screen is how a later drag lands somewhere surprising.
  const at = phase.days.findIndex((existing) => (existing.dayOfWeek ?? 0) > dayOfWeek);
  if (at !== -1) phase.days.splice(at, 0, day);
  else phase.days.push(day);
}

/**
 * Why no new day may be added to this phase, or `null` when one may.
 *
 * · `phaseFull` — `PostPhaseTemplateInput.Days` caps a phase at 14.
 * · `closedToNewDays` — a running fork whose client has already lived through EVERY cycle of this
 *   phase. The backend does not refuse the save: a slot its record has never seen claims nothing
 *   about the past. But regeneration only reaches forward, so in a cycle the record has closed to new
 *   days nothing will ever mint an occurrence of the day, and the plan would show a training the
 *   client is never given. When only SOME of the phase's cycles are closed the day is accepted and
 *   simply starts in the first open one. Mobile's `addDayGate` (`structuralEditFlow.ts`) is the same
 *   rule over the same field.
 *
 * `cyclesClosedToNewDays` of `null` (no full read, no client) or `[]` closes nothing, so a library
 * template and an untrained fork both fall straight through. The phase's cycles are recomputed from
 * the phase lengths rather than read off `PhaseDraft.startCycle`, which a length edit leaves stale.
 */
export type DayAdditionRefusal = 'phaseFull' | 'closedToNewDays';

export function dayAdditionRefusal(
  draft: PlanDraft,
  phaseIndex: number,
): DayAdditionRefusal | null {
  const phase = draft.phases[phaseIndex];
  if (phase === undefined) return null;
  if (phase.days.length >= COLLECTION_CAPS.daysPerPhase.max) return 'phaseFull';
  const closed = draft.live.cyclesClosedToNewDays;
  if (!draft.live.isForkTemplate || closed === null || closed.length === 0) return null;
  const closedSet = new Set(closed);
  const start = phaseStartCycles(draft.phases)[phaseIndex] ?? 1;
  for (let cycle = start; cycle < start + Math.max(1, phase.cycles); cycle += 1) {
    if (!closedSet.has(cycle)) return null;
  }
  return 'closedToNewDays';
}

/**
 * The token of the day a just-run `addDay` created, so the caller can open the dock on it.
 *
 * One function rather than two because "which day did I just make" is one question, and the answer
 * differs only by how the mode PLACES a day: a date plan puts it on the weekday it was asked for, a
 * sequence plan appends it. A caller that had to know which lookup applies would be re-deriving the
 * mode rule that `addDay` already owns.
 *
 * `null` when the cap refused the add, which is exactly what makes it safe to call unconditionally:
 * the tape at fourteen days answers `null` and no dock opens over a day that was never made. (On a
 * date plan a refused add over an occupied weekday answers with the day ALREADY there, which is the
 * honest answer to the question asked — that weekday's day — and the affordance is only drawn on an
 * empty column anyway.)
 */
export function addedDayToken(
  draft: PlanDraft,
  phaseIndex: number,
  dayOfWeek: number | null,
): string | null {
  const phase = draft.phases[phaseIndex];
  if (phase === undefined) return null;
  if (draft.mode !== 'date') return phase.days[phase.days.length - 1]?.slotToken ?? null;
  return phase.days.find((day) => day.dayOfWeek === dayOfWeek)?.slotToken ?? null;
}

/**
 * Move the gap AFTER a day — the tape's rest column and its −/+ stepper.
 *
 * A **plain field patch**, and deliberately so: mobile's reducer writes `restDaysAfter` through its
 * generic `updateDay` with no restamping and no side effects (`planDraftReducer.ts`, the `updateDay`
 * case), and this is that. Nothing about it touches an exercise, a set or a rest column — and,
 * since `self-paced-plan-requirements.md` R2, nothing about it touches another day either. It is an
 * advisory recovery note the client's own finish timestamp is measured from (R9); it moves no date,
 * here or at materialisation.
 *
 * Clamped to `REST_DAYS_AFTER_RANGE`, which is the BACKEND's bound (`PostDayTemplateInput`) rather
 * than the phone's stepper range — see the slice `AGENTS.md` for why the two differ. A value that
 * is not a whole number is refused outright rather than rounded: the wire's field is an integer and
 * a silent round is a number the trainer did not type.
 *
 * A no-op write records no patch, so pressing − at zero does not mark the plan unsaved.
 */
export function setRestDaysAfter(draft: MutablePlan, daySlotToken: string, days: number): void {
  if (!Number.isInteger(days)) return;
  const day = findDay(draft, daySlotToken);
  if (day === null) return;
  day.restDaysAfter = Math.min(
    Math.max(days, REST_DAYS_AFTER_RANGE.min),
    REST_DAYS_AFTER_RANGE.max,
  );
}

/**
 * Move a day to a POSITION within its phase — the tape's reorder.
 *
 * The sequence counterpart of `moveDayToWeekday`, and it answers identity the same way: the day
 * carries its `slotToken`, its `serverId` and its `serverOrder` to the new position. `slotToken` is
 * what `WorkoutExecutionPropagator` matches a not-started workout by while the occurrence is in its
 * own phase, and `serverOrder` is the only witness left that the day used to sit somewhere else —
 * which is precisely what `hasReorderedDay` reads to raise the sequence-recalc consent.
 *
 * **`Order` is renumbered, and that is correct here.** Day `Order` is assigned from array position
 * at serialisation (`draftToRequest`), which is what both clients do and what the backend's own
 * comment assumes — see the slice `AGENTS.md`, which records that the "never renumber" rule is a
 * PHASE-level rule and that the day level is matched by token.
 *
 * `targetIndex` is a POSITION among the phase's days, clamped into the array. A move to where the
 * day already stands writes nothing, so Immer records no patch and a keyboard lift dropped on its
 * own position leaves a byte-identical tree.
 */
export function moveDayToPosition(
  draft: MutablePlan,
  daySlotToken: string,
  targetIndex: number,
): void {
  if (draft.mode === 'date') return;
  for (const phase of draft.phases) {
    const from = phase.days.findIndex((day) => day.slotToken === daySlotToken);
    if (from === -1) continue;
    const to = Math.min(Math.max(targetIndex, 0), phase.days.length - 1);
    if (to === from) return;
    const [day] = phase.days.splice(from, 1);
    if (day === undefined) return;
    phase.days.splice(to, 0, day);
    return;
  }
}

/**
 * The trainer's note on the SESSION — real, server-stored, and snapshotted onto the client's
 * workout, which is why the canon labels it "widoczna dla klienta na starcie treningu".
 *
 * PR-B1 read and re-emitted this field with no editor above it, precisely so that every save would
 * stop deleting a note authored on the phone. This is that editor.
 */
export function setSessionNotes(draft: MutablePlan, daySlotToken: string, notes: string): void {
  const day = findDay(draft, daySlotToken);
  if (day === null) return;
  const trimmed = notes.trim();
  day.sessionNotes = trimmed.length > 0 ? trimmed : null;
}

/**
 * Move the day's default rest, and RESTAMP every exercise that was following it.
 *
 * "Following" is asked of the VALUES against the rest the day holds NOW, never against the value it
 * is about to be given — every follower differs from the new number, so comparing forwards would
 * restamp nothing. An exercise carrying its own rest is left alone whatever its column is doing,
 * which is the only formulation that survives D12 hiding the column on a superset member that still
 * carries a trainer-authored number.
 */
export function setDayRest(draft: MutablePlan, daySlotToken: string, seconds: number): void {
  const day = findDay(draft, daySlotToken);
  if (day === null) return;
  const next = clampDayRest(seconds);
  const current = dayRestSeconds(day.restSeconds);
  if (next === current && day.restSeconds !== null) return;

  for (const exercise of day.exercises) {
    if (overridesDayRest(exercise.sets, current)) continue;
    for (const set of exercise.sets) {
      if (set.prescribedRestTime !== next) set.prescribedRestTime = next;
    }
  }
  day.restSeconds = next;
  refreshRestColumns(day);
}

/**
 * Show or hide one exercise's REST column.
 *
 * Rest is not a measurement field, so it has neither a reveal list nor a clearing rule of its own.
 * Every set always carries a concrete rest, and HIDING the column restamps them back to the day's
 * value (D4) — leaving them would make the next load derive the column as still-overridden and
 * bring it straight back, so the trainer's action would appear not to have worked.
 */
export function toggleRestColumn(draft: MutablePlan, exerciseSlotToken: string): void {
  const site = findExerciseSite(draft, exerciseSlotToken);
  if (site === null) return;
  if (!site.exercise.showsRestColumn) {
    site.exercise.showsRestColumn = true;
    return;
  }
  site.exercise.showsRestColumn = false;
  const rest = dayRestSeconds(site.day.restSeconds);
  for (const set of site.exercise.sets) {
    if (set.prescribedRestTime !== rest) set.prescribedRestTime = rest;
  }
}

/**
 * Open a measurement column the exercise MEASURES but does not show.
 *
 * The column has nothing behind it yet — that is exactly why it is hidden — so the reveal is the
 * only thing that can open it, and it is remembered for this editing session alone. The moment the
 * trainer types, the value takes over and the reveal is redundant.
 *
 * Guarded against the contract here rather than trusted from the caller: a column outside what the
 * exercise measures would render a cell whose value the wire has nowhere to put.
 */
export function revealColumn(
  draft: MutablePlan,
  exerciseSlotToken: string,
  field: MeasurementFieldKey,
): void {
  const exercise = findExercise(draft, exerciseSlotToken);
  if (exercise === null) return;
  if (!exercise.measurementFields.includes(field)) return;
  if (exercise.revealedColumns.includes(field)) return;
  exercise.revealedColumns.push(field);
}

/**
 * Take a measurement column away: clear what is behind it — sets and per-cycle pins alike — and
 * drop it from the revealed list.
 *
 * The reveal has to go with the values. Leaving it would hold the column open with nothing in it,
 * so the removal would appear to do nothing at all until the exercise was reloaded.
 *
 * What is cleared, and what is deliberately left standing, is `removeColumnValues`' decision —
 * this operation is the write, not the rule. REST is not a measurement field and does not come
 * here: it has its own flag and its own restamp (`toggleRestColumn`).
 */
export function hideColumn(
  draft: MutablePlan,
  exerciseSlotToken: string,
  field: MeasurementFieldKey,
): void {
  const exercise = findExercise(draft, exerciseSlotToken);
  if (exercise === null) return;
  const next = removeColumnValues(exercise, field);
  next.sets.forEach((set, index) => {
    Object.assign(exercise.sets[index] as SetDraft, set);
  });
  // The cast is Immer's variance, not a silenced error: `removeColumnValues` returns the plan's own
  // `readonly`-fielded overrides and a draft array is typed as writable ones. The values are the
  // same objects the rule selected out of this very draft.
  exercise.progressionOverrides = next.progressionOverrides as Draft<ProgressionOverride>[];
  exercise.revealedColumns = exercise.revealedColumns.filter((column) => column !== field);
}

// ── Progression ──────────────────────────────────────────────────────────────────────────────


/** Whether a cell on this row may be pinned at all — the two rows the backend refuses an override on. */
function canPinCell(sets: readonly SetDraft[], setIndex: number): boolean {
  const set = sets[setIndex - 1];
  return set !== undefined && countsAsWork(set.setType);
}

/**
 * What the rule sheet's „Zastosuj do draftu" carries: one column's rule, and the band the trainer
 * authored beside it.
 *
 * ONE ACT, because it is one decision. Picking a family, naming the column the client climbs and
 * typing a step is three clicks and one prescription; writing each click through would put three
 * entries on the undo stack, recompute the matrix twice on the way to a state nobody asked to see,
 * and let the illegal in-between shapes — a band on a column that still carries its own rule — exist
 * for a render. The sheet stages the whole thing and commits it here.
 */
export interface ExerciseAuthoring {
  /** The column the rule WRITES — the key it sits under. */
  readonly field: MeasurementFieldKey;
  /** The rule, or null to take this column's rule off. */
  readonly rule: ProgressionRule | null;
  /**
   * The band to write across the rows, or null to leave them exactly where the rule write left them.
   *
   * Present only when the trainer actually edited the range field: the FLOOR is the row's own
   * prescription and a wave of 10 / 8 / 6 is programming, so a band merely looked at must not
   * flatten it. `overwriteFloor` says the pair was TYPED as one, which is the only case where the
   * floor is the sheet's to write over.
   */
  readonly band: {
    readonly gatedField: MeasurementFieldKey;
    readonly floor: number | null;
    readonly ceiling: number | null;
    readonly overwriteFloor?: boolean;
  } | null;
  /**
   * Take every per-cycle pin off THIS column as part of the same act — the sheet's „Brak progresji".
   *
   * It rides on the authoring rather than being a second operation the sheet calls afterwards,
   * because the trainer made ONE choice and must get one undo entry for it: „Brak progresji" says
   * "this column is flat and nothing is hand-set on it", which is a rule removal and a pin removal
   * that are only meaningful together. Two calls would leave an undo step where the rule is gone and
   * the pins are not — a state nobody asked to see and the matrix would draw.
   *
   * It is scoped to `field`, unlike `clearExercisePins`, which is the foot's „Wyczyść nadpisania"
   * and deliberately reaches every column. Ladder percents are not pins on any column and survive
   * either way — `clearColumnPins`'s own rule.
   */
  readonly clearPins?: boolean;
}

/**
 * Apply one exercise's progression authoring — **the ONE write that changes what the matrix
 * projects**, and the one undo entry the trainer gets for it.
 *
 * ── It takes a LIST, because one session authors several columns ─────────────────────────────
 * The sheet lets a trainer move between every column the movement measures and edit the rule on
 * each one before pressing „Zastosuj do draftu". Committing only the column that happened to be on
 * screen dropped every other staged edit under an announcement saying the rule had been applied —
 * silently, because the sheet closed on the same press. The whole list lands inside ONE
 * `runOperation`, so the trainer still gets one undo entry for one decision.
 *
 * Each authoring is judged on its own: `ruleOnColumnPatch` refuses a column the movement does not
 * measure or one another rule already bands, and a REFUSED authoring applies nothing — neither its
 * rule nor its band — while the rest of the list still lands. Applying the band anyway over a
 * refused rule would leave ceilings under no rule at all.
 *
 * Within one authoring the band is written AFTER the rule, over the rows the rule write left
 * behind, because those two are not independent: taking an execution-gated rule off clears every
 * ceiling, and a band written first would be erased by the clearing it caused.
 */
export function applyExerciseAuthoring(
  draft: MutablePlan,
  exerciseSlotToken: string,
  authorings: readonly ExerciseAuthoring[],
): void {
  const exercise = findExercise(draft, exerciseSlotToken);
  if (exercise === null) return;

  for (const authoring of authorings) {
    const written = ruleOnColumnPatch(
      {
        progressionRules: exercise.progressionRules,
        measurementFields: exercise.measurementFields,
        sets: exercise.sets,
      },
      authoring.field,
      authoring.rule,
    );
    if (written.refusal !== null) continue;

    if (written.patch.progressionRules !== undefined) {
      exercise.progressionRules = written.patch.progressionRules;
    }
    const ruled: readonly SetDraft[] = written.patch.sets ?? exercise.sets;
    const banded =
      authoring.band === null
        ? ruled
        : writeBand(
            ruled,
            authoring.band.gatedField,
            authoring.band.floor,
            authoring.band.ceiling,
            authoring.band.overwriteFloor ?? false,
          );
    if (banded !== exercise.sets) exercise.sets = [...banded];

    /* LAST within the authoring, and after the rows are settled: `clearColumnPins` reads the sets to
     * tell a ladder percent from a pin, so it has to see the rows the rule write and the band left
     * behind rather than the ones they replaced. A column the exercise holds no axis for has no pins
     * to take off, which is why the null is simply skipped. */
    if (authoring.clearPins === true) {
      const axis = axisForField(authoring.field, exercise.prescriptionMode);
      if (axis !== null) {
        exercise.progressionOverrides = clearColumnPins(
          exercise.progressionOverrides,
          exercise.sets,
          axis,
        );
      }
    }
  }
}

/**
 * The axis a pin is written on: the column the grid drew the cell in, or the exercise's own where the
 * caller names none. A column the exercise cannot hold an axis for falls back the same way, so the
 * write is never addressed at nothing.
 */
function axisOfAddress(exercise: ExerciseDraft, address: MatrixCellAddress): ProgressionAxis {
  return (
    (address.field === undefined
      ? null
      : axisForField(address.field, exercise.prescriptionMode)) ?? axisForExercise(exercise)
  );
}

/**
 * Pin one matrix cell, or clear it — the cell click and the ladder percent both land here.
 *
 * The two write paths differ because a LADDER member's cell is not a value on the progression axis:
 * it is the descent's "% of the top", the only per-cycle prescription the backend accepts on such a
 * row (R10). So the axis plays no part there and `commitBackoffPercent` decides, which is also what
 * gives a blank on a later cycle its fall-back-to-base meaning. Everywhere else the exercise's own
 * axis selects the one field the pin is written into — see `MatrixCellAddress` on why it is read
 * here rather than handed in.
 *
 * The FIRST cycle of the exercise's window is not pinnable and is refused here rather than guarded
 * at the renderer: that column IS the authored base, and pinning it would produce an override that
 * duplicates the set's own prescription — two sources for one number, which the next load then has
 * to choose between. Editing it means editing the set, in the set table.
 *
 * ── A value equal to what the rule already computes CLEARS the cell ──────────────────────────
 * Ported from mobile's `commitCell`, and it is not a nicety: the matrix commits on BLUR, so opening
 * a computed cell and clicking away anywhere commits the number that was already showing. Without
 * the guard that pins it — the cell turns accent-orange, the legend grows a "ręcznie" row, and the
 * value stops following the rule, all from a click the trainer made to look at something else. Worse
 * silently: the pin is then PERSISTED, so a later change to the delta moves every cell except the
 * ones that were glanced at.
 *
 * The comparison is against the SNAPPED projection — what the cell actually displays — so typing
 * back the rounded number on screen clears rather than re-pins, and it runs on the NORMALISED value
 * so an out-of-range typing that clamps onto the projection clears too. The offset runs from the
 * exercise's own base cycle, which is the column the value was rendered in.
 */
export function setMatrixOverride(
  draft: MutablePlan,
  exerciseSlotToken: string,
  address: MatrixCellAddress,
  valueRaw: number | null,
): void {
  const exercise = findExercise(draft, exerciseSlotToken);
  if (exercise === null) return;
  if (address.cycleIndex <= address.baseCycle) return;
  if (!canPinCell(exercise.sets, address.setIndex)) return;

  const set = exercise.sets[address.setIndex - 1];
  if (set === undefined) return;

  if (derivesLoadInSession(set)) {
    exercise.progressionOverrides = commitBackoffPercent(
      exercise.progressionOverrides,
      set.prescribedPercent,
      address.setIndex,
      address.cycleIndex,
      valueRaw,
    );
    return;
  }

  const axis = axisOfAddress(exercise, address);
  const pinned = valueRaw === null ? null : normaliseTypedValue(valueRaw, axis);
  // EXPOSURES, not calendar distance: a cycle the exercise sits out advances the rule by nothing, so
  // `cycleIndex − baseCycle` would compare the typed value against a projection the backend will
  // never materialise — and a mismatch here does not merely mis-render, it decides whether the pin
  // is kept or cleared.
  //
  // And on a live fork the projection is the RECORD-AWARE one the grid draws (`chainAnchorOf`):
  // comparing against the base's would pin the number a glance committed on blur, and clear the one
  // a trainer typed to give a cycle the step the record already refused.
  const lifespan = findExerciseLifespan(draft.phases, exerciseSlotToken) ?? { cycles: [] };
  const offset = exposureOffsetOf(lifespan, address.cycleIndex);
  const base = axisBaseOf(set, axis);
  const projected =
    offset === null
      ? null
      : projectFromAnchor(
          chainAnchorOf(exercise, set, address.setIndex, lifespan, axis, base),
          offset,
          base,
          ruleFromExercise(exercise, axis),
          axis,
        );

  exercise.progressionOverrides =
    pinned !== null && projected !== null && Math.abs(pinned - projected) < 1e-6
      ? clearOverride(exercise.progressionOverrides, address.setIndex, address.cycleIndex)
      : upsertOverride(
          exercise.progressionOverrides,
          address.setIndex,
          address.cycleIndex,
          pinned,
          axis,
        );
}

/**
 * Write the BASE value one matrix cell is drawn from — what editing the C1 („BAZA") column means.
 *
 * ── It is not a pin, and that is the whole distinction ───────────────────────────────────────
 * Every other column of the matrix is a PROJECTION of this one, so a number typed into C1 is not a
 * correction to one cycle: it is the row's own prescription, the same number the set table two boxes
 * away edits, and every later cycle re-projects from it. Writing it as an override would produce a
 * second source for one number — the set's prescription and a pin duplicating it — and the next load
 * would have to choose between them.
 *
 * ── The refusal it replaces was withdrawn by the owner on 15 September 2026 ───────────────────
 * `setMatrixOverride` refuses the base column and says why, and until this date `matrixCellState`
 * refused to open an editor there at all, on the reasoning that the value „belongs to the set
 * table". Mobile has always allowed it (`progressionCellEdit.ts`, the `baseColumn` /
 * `basePercentOfMax` / `basePercentOfTop` voices), and the owner's call is that the web does the
 * same: the trainer is looking straight at the number, and sending them to another surface to change
 * it is the kind of rule an app knows and a person does not.
 *
 * ── ONE WRITER ───────────────────────────────────────────────────────────────────────────────
 * The value lands through `setSetField`, which is the operation the set table itself uses — so the
 * AMRAP at-most-one-cap rule and the position/`setIndex` agreement check apply to a number typed in
 * the matrix exactly as they do to one typed in the table. Nothing about a prescription is decided
 * twice.
 *
 * Three refusals remain, and none of them is about the base column:
 * · a row the backend takes no prescription on (`canPinCell` — a warm-up, anything not work),
 * · a consumed cycle, which is behind the client; a template has none,
 * · an EMPTY field, which is a no-op here rather than a clear. Mobile answers the same way
 *   (`readCellOverride`: an empty base cell reads `isUnchanged`, and the CTA refuses it), and the
 *   reason is that a base cell carries no pin to hand back — emptying it would delete the row's
 *   prescription from a gesture that means "cancel" on every other cell of the grid.
 */
export function setMatrixBaseValue(
  draft: MutablePlan,
  exerciseSlotToken: string,
  address: MatrixCellAddress,
  valueRaw: number | null,
): void {
  const exercise = findExercise(draft, exerciseSlotToken);
  if (exercise === null) return;
  // The BASE column and no other. A caller aiming this at a later cycle is asking for a pin, which
  // is `setMatrixOverride`'s question, and answering it here would move the whole row instead.
  if (address.cycleIndex !== address.baseCycle) return;
  if (!canPinCell(exercise.sets, address.setIndex)) return;
  if (valueRaw === null) return;

  const set = exercise.sets[address.setIndex - 1];
  if (set === undefined) return;

  // A descent's base is its share of the top set, on the fraction column, whatever the grid's own
  // column is — the same reading `setMatrixOverride` gives such a row, and mobile's
  // `basePercentOfTop`. The load axis plays no part in it.
  const axis = derivesLoadInSession(set) ? PERCENT_AXIS : axisOfAddress(exercise, address);
  setSetField(
    draft,
    { exerciseSlotToken, setIndex: address.setIndex },
    { [axis.setField]: normaliseTypedValue(valueRaw, axis) },
  );
}

/** Drop one cell's pin, handing it back to the rule. A cell with no pin is a no-op, not an error. */
export function revertMatrixOverride(
  draft: MutablePlan,
  exerciseSlotToken: string,
  address: Pick<MatrixCellAddress, 'setIndex' | 'cycleIndex'>,
): void {
  const exercise = findExercise(draft, exerciseSlotToken);
  if (exercise === null) return;
  exercise.progressionOverrides = clearOverride(
    exercise.progressionOverrides,
    address.setIndex,
    address.cycleIndex,
  );
}

/**
 * Hand EVERY pinned cell on one exercise back to its rule — the sheet's „Wyczyść nadpisania", as one
 * draft operation and one undo entry.
 *
 * It is one act because the trainer made one press. Reverting cell by cell would put a pin's worth
 * of history on the stack per cell and leave an undo that walks the grid back one square at a time,
 * which is not what the button says it does.
 *
 * EVERY COLUMN CARRYING A PIN IS CLEARED, not only the one the exercise progresses today. It used to
 * be the latter, derived here so that no surface could hand an axis in — and that was right while a
 * pin could only ever be written on the exercise's own column. Since 15 September 2026 the sheet's
 * grid follows the trainer's chips and `MatrixCellAddress` carries the column, so pins on two columns
 * are a shape the app can produce; a button reading „Wyczyść nadpisania" that silently left half of
 * them standing would be the same defect one layer up. The columns are read off the OVERRIDES
 * themselves rather than off the rules, so a pin left behind by a rule that has since moved is
 * reachable too.
 *
 * Ladder percents are not pins on any column and survive, which is `clearColumnPins`'s own rule.
 */
export function clearExercisePins(draft: MutablePlan, exerciseSlotToken: string): void {
  const exercise = findExercise(draft, exerciseSlotToken);
  if (exercise === null) return;
  let overrides = exercise.progressionOverrides;
  for (const kind of pinnedColumns(overrides, exercise.sets)) {
    // The KIND already says which reading of the load column the pin is on — `pinnedColumns` answers
    // `'percent'` for a fraction and `'weight'` for kilograms — so the axis is resolved from it
    // directly rather than through the exercise's prescription mode, which would answer for one of
    // the two and leave the other standing.
    const axis = kind === 'percent' ? PERCENT_AXIS : axisForField(kind, PRESCRIPTION_MODE.absolute);
    if (axis !== null) overrides = clearColumnPins(overrides, exercise.sets, axis);
  }
  exercise.progressionOverrides = overrides;
}

/**
 * Turn the pin on one cell into a BASE SHIFT — the calendar day editor's
 * „ten tydzień i wszystkie kolejne".
 *
 * The trainer typed a value into a week and then said they meant it for that week and every one
 * after. That is not a pin and it is not a flat base write: the curve has to pass through the typed
 * number at THIS cycle and keep climbing at the same rate afterwards, which makes it the inverse of
 * the projection (`cycleRebase.ts`).
 *
 * Two things happen here and both are the inversion's own answer, never this function's judgement:
 * the base moves, and the pin is dropped only when the projection lands back exactly on what was
 * typed. Where snapping means it does not, the pin STAYS — so the open week reads what the trainer
 * entered while the following weeks continue from the shifted curve.
 *
 * A no-op where there is nothing pinned: the caller is the save path, which asks about every cell it
 * touched, and a cell it touched with a base write has no override to convert.
 */
export function rebaseOverrideForward(
  draft: MutablePlan,
  exerciseSlotToken: string,
  address: MatrixCellAddress,
): void {
  const exercise = findExercise(draft, exerciseSlotToken);
  if (exercise === null) return;
  const shift = rebaseToCycle(exercise, address.setIndex, address.cycleIndex, address.baseCycle);
  if (shift === null) return;
  const set = exercise.sets[address.setIndex - 1];
  if (set === undefined) return;

  switch (shift.field) {
    case 'prescribedWeight':
      set.prescribedWeight = shift.base;
      break;
    case 'prescribedPercent':
      set.prescribedPercent = shift.base;
      break;
    case 'prescribedRpe':
      set.prescribedRpe = shift.base;
      break;
    default:
      set.prescribedRir = shift.base;
      break;
  }

  if (!shift.keepsOverride) {
    exercise.progressionOverrides = clearOverride(
      exercise.progressionOverrides,
      address.setIndex,
      address.cycleIndex,
    );
  }
}

// ── Moves (PR-E) ─────────────────────────────────────────────────────────────────────────────
//
// Three acts, and one rule they all obey: **a caller reports a target INDEX or a target WEEKDAY,
// and this layer decides every position.** No caller writes an `order`, because the draft has none
// — day and exercise positions are the array's, assigned as `index + 1` at serialisation. The level
// where position is load-bearing is PHASES, and nothing here moves one.
//
// What DOES travel is identity, and the two moves answer it differently on purpose:
//
// · A **day** carries its `slotToken`, its `serverId` and its `serverOrder` to the new weekday.
//   `WorkoutExecutionPropagator.ResolveTarget` matches a not-started workout by that token while
//   the occurrence is in its own phase, so the client's already-scheduled Monday workout follows
//   the day to Friday instead of being deleted and recreated. Mobile does the same through
//   `updateDay({ dayOfWeek })` — a plain field patch that re-mints nothing.
// · An **exercise** crossing into another DAY does NOT carry its slot. View ADR §11 case 1:
//   "Removal plus addition. The slot is not carried across, so the exercise's activation cycle and
//   dynamic chain restart (R12)". Within one day it keeps everything, because nothing about its
//   prescription changed — that is view ADR §11 case 2, "the slot is preserved, so nothing about
//   progression changes".
//
// **Mobile has no cross-day exercise move at all** — verified in `planDraftReducer.ts`, whose whole
// action union addresses an exercise either by token alone or inside one `dayTempId`. So there was
// no parity to replicate for the re-mint, and the ADR is what decides it. Recorded here rather than
// left implicit, because "replicate mobile verbatim" is the standing rule and this is a place where
// there was nothing to replicate.

/**
 * The array indices each main-work BLOCK holds, in the order the canvas and the dock draw them.
 *
 * The blocking comes from `dayCard` rather than being re-derived, and that is the point: a superset
 * is ONE list position with two members, a warm-up bracketed to a lift is a mini-group, and a group
 * that cannot be drawn as either degenerates into singles. Those three outcomes live in
 * `weekProjection.toBlocks` and a second copy of them here would disagree with the screen on the
 * first degenerate group — which is precisely a drop landing somewhere the trainer did not aim.
 *
 * Standalone day warm-ups are absent, because they are absent from the main-work list the trainer
 * drags in. They have no grip: the canon draws the warm-up block as a name list with no parameters
 * and no position numbers. A block crosses INTO it by the ⋯ role toggle or by a drop on the warm-up
 * zone (`moveExerciseBlockToWarmup`), and back OUT only by the toggle.
 */
function workBlockIndices(day: DayDraft): number[][] {
  const positionOf = new Map(day.exercises.map((exercise, index) => [exercise.slotToken, index]));
  return dayCard(day).blocks.map((block) =>
    (block.kind === 'single' ? [block.exercise] : block.members)
      .map((row) => positionOf.get(row.slotToken))
      .filter((index): index is number => index !== undefined),
  );
}

/**
 * How many main-work blocks a day draws — the number of drop positions minus one, and the "z N" of
 * every announcement. Exported because the surfaces that announce a move must not count blocks
 * themselves.
 */
export function workBlockCount(day: DayDraft): number {
  return workBlockIndices(day).length;
}

/** Where an exercise's block sits, and how many rows travel with it. */
export interface WorkBlockSite {
  /** Zero-based position among the day's main-work blocks — the "3" of "pozycja 3 z 5". */
  readonly position: number;
  /** How many exercises the grip carries: 1 for a single, 2+ for a superset or a mini-group. */
  readonly size: number;
}

/**
 * The block an exercise belongs to, or `null` when it belongs to none — a standalone day warm-up,
 * which occupies no main-work position and carries no grip.
 */
export function workBlockOf(day: DayDraft, exerciseSlotToken: string): WorkBlockSite | null {
  const index = day.exercises.findIndex((exercise) => exercise.slotToken === exerciseSlotToken);
  if (index === -1) return null;
  const blocks = workBlockIndices(day);
  const position = blocks.findIndex((indices) => indices.includes(index));
  const block = blocks[position];
  return block === undefined ? null : { position, size: block.length };
}

/**
 * The ARRAY index a block-position drop lands at.
 *
 * Past the last block it is the end of the work run rather than the end of the array, so a drop at
 * the bottom of a day whose exercises are `[warm-up, work]` cannot land underneath a warm-up that
 * sits below the work on a tree this app did not author.
 */
function insertionIndex(blocks: readonly number[][], blockIndex: number, length: number): number {
  const clamped = Math.min(Math.max(blockIndex, 0), blocks.length);
  const block = blocks[clamped];
  if (block !== undefined) return block[0] ?? length;
  const last = blocks[blocks.length - 1];
  const tail = last?.[last.length - 1];
  return tail === undefined ? length : tail + 1;
}

/**
 * Move a main-work block to a position — within its own day, or into another day of the same phase.
 *
 * The unit that moves is the BLOCK, not the row: a superset dragged by its grip carries both
 * members, because the canon's own note says a superset is "jeden numer pozycji" — one programming
 * decision. `exerciseSlotToken` may name any member; the block it belongs to is what travels. That
 * is also mobile's reading (`DayBody.handleReorder` reorders blocks and flattens afterwards).
 *
 * **Cross-PHASE is refused.** A progression chain does not cross a phase boundary and an override's
 * `cycleIndex` is absolute, so an exercise landing in another phase would carry pins addressed at
 * cycles it is no longer prescribed in. No surface can produce such a drop — the week shows one
 * phase and the dock's days come from it — so this is a guard rather than a path.
 *
 * A drop onto the block's own position, or into the gap immediately after it, rewrites the array to
 * exactly what it was. Immer records no patch for that, so it costs no undo entry and does not mark
 * the plan unsaved.
 */
export function moveExercise(
  draft: MutablePlan,
  targetDaySlotToken: string,
  exerciseSlotToken: string,
  targetBlockIndex: number,
): void {
  const site = findExerciseSite(draft, exerciseSlotToken);
  const target = findDaySite(draft, targetDaySlotToken);
  if (site === null || target === null) return;
  if (target.phase !== site.phase) return;

  const sourceBlocks = workBlockIndices(site.day);
  const moving = sourceBlocks.find((indices) => indices.includes(site.index));
  if (moving === undefined) return;

  const descending = [...moving].sort((left, right) => right - left);

  if (site.day.slotToken === target.day.slotToken) {
    const list = site.day.exercises;
    const anchor = insertionIndex(sourceBlocks, targetBlockIndex, list.length);
    const lifted = moving.map((index) => list[index]).filter((node) => node !== undefined);
    for (const index of descending) list.splice(index, 1);
    // Every lifted row that sat ABOVE the anchor has left, so the anchor slides up by that many.
    const removedAbove = moving.filter((index) => index < anchor).length;
    list.splice(anchor - removedAbove, 0, ...lifted);
    normaliseGroups(site.day);
    refreshRestColumns(site.day);
    return;
  }

  if (
    target.day.exercises.length + moving.length >
    COLLECTION_CAPS.exercisesPerSession.max
  ) {
    return;
  }

  // A NEW slot in the new day. `transplantedExercise` mints the token, drops the server-assigned
  // activation cycle and copies the override list by value — which is what makes the landing read
  // as a first exposure rather than as the same slot with a different parent. It is NOT
  // `cloneExercise`: a copy starts fresh inside its day's window, a move carries the trainer's own
  // retirement and holes with it, and reusing the copy here widened every moved exercise back over
  // the weeks the trainer had cut.
  const group = target.day.exercises.some((exercise) => exercise.group)
    ? nextGroupId(target.day)
    : null;
  const rest = dayRestSeconds(target.day.restSeconds);
  const arriving = moving
    .map((index) => site.day.exercises[index])
    .filter((node): node is Draft<ExerciseDraft> => node !== undefined)
    .map((node) => {
      const copy = transplantedExercise(node);
      // The bracket letter is re-minted in the TARGET day's alphabet. Carrying "A" into a day that
      // already has an "A" would fuse two unrelated pairs the moment they became adjacent.
      const regrouped = copy.group == null ? copy : { ...copy, group: group ?? copy.group };
      return restampExerciseRest(regrouped, rest) as Draft<ExerciseDraft>;
    });

  for (const index of descending) site.day.exercises.splice(index, 1);
  const anchor = insertionIndex(
    workBlockIndices(target.day),
    targetBlockIndex,
    target.day.exercises.length,
  );
  target.day.exercises.splice(anchor, 0, ...arriving);

  normaliseGroups(site.day);
  refreshRestColumns(site.day);
  normaliseGroups(target.day);
  refreshRestColumns(target.day);
}

/**
 * Move a main-work block into its own day's warm-up run — the block dropped on the warm-up zone.
 *
 * The drag counterpart of `setExerciseRole(…, 'Warmup')` (owner, 29 October 2026), and the same
 * outcome for a single exercise: role `Warmup`, no bracket, appended after the standalone warm-ups
 * already there, every set, note and rule kept.
 *
 * The unit is the BLOCK, as for every other drag, so a superset dropped by its grip lands as two
 * warm-ups. Every member is unbracketed BEFORE its role changes, which is why this does not loop over
 * `setExerciseRole`: changing a mini-group's lift first would leave its bound warm-up orphaned, and
 * `normaliseGroups` deletes an orphaned bound warm-up. Here both arrive as standalone warm-ups, in
 * the order the block drew them.
 */
export function moveExerciseBlockToWarmup(draft: MutablePlan, exerciseSlotToken: string): void {
  const site = findExerciseSite(draft, exerciseSlotToken);
  if (site === null) return;
  const { day } = site;
  const moving = workBlockIndices(day).find((indices) => indices.includes(site.index));
  if (moving === undefined) return;

  const lifted = moving
    .map((index) => day.exercises[index])
    .filter((node): node is Draft<ExerciseDraft> => node !== undefined);
  for (const index of [...moving].sort((left, right) => right - left)) {
    day.exercises.splice(index, 1);
  }
  for (const node of lifted) {
    node.role = 'Warmup';
    node.group = null;
  }
  let at = 0;
  while (at < day.exercises.length && isStandaloneDayWarmup(day.exercises[at])) at += 1;
  day.exercises.splice(at, 0, ...lifted);
  normaliseGroups(day);
  refreshRestColumns(day);
}

/**
 * Insert a library pick at a block position — the rail dragged into the exercise list.
 *
 * The append path is `addExercise`, which is what a press on the rail row still does. This one
 * exists because a drag reports a position and an append would silently ignore it.
 */
export function insertExerciseAt(
  draft: MutablePlan,
  daySlotToken: string,
  pick: ExercisePick,
  targetBlockIndex: number,
): string | null {
  const day = findDay(draft, daySlotToken);
  if (day === null) return null;
  if (day.exercises.length >= COLLECTION_CAPS.exercisesPerSession.max) return null;

  const rest = dayRestSeconds(day.restSeconds);
  // Cast at the construction seam — see `addExercise`.
  const exercise = restampExerciseRest(createExercise(pick, 'Main'), rest) as Draft<ExerciseDraft>;
  const at = insertionIndex(workBlockIndices(day), targetBlockIndex, day.exercises.length);
  day.exercises.splice(at, 0, exercise);
  refreshRestColumns(day);
  // NAMES what it created, exactly as `addExercise` does and for the same caller: a library lying
  // over the day has to send focus to the card the press just made.
  return exercise.slotToken;
}

/**
 * Move a training day to another weekday of its own phase — the session card dragged between
 * columns.
 *
 * **The day keeps every scrap of its identity.** `slotToken` is what the backend matches a
 * not-started workout by, `serverId` tells the structural preview an existing day from a new one,
 * and `serverOrder` is the only witness left that the day used to sit somewhere else — a reorder
 * PRESERVES both, which is exactly what `hasReorderedDay` reads. Re-minting any of the three would
 * make the client's scheduled workout read as a deletion plus an addition.
 *
 * **An occupied target SWAPS.** Two days may not share a weekday: `addDay` refuses it and
 * `weekProjection` can only report the second one as overflow. Refusing the drop instead would make
 * the busiest weeks — the ones where every column is programmed — the ones where nothing can be
 * rearranged, and dropping the target's day would destroy training the trainer did not touch.
 *
 * The array is re-sorted into weekday order afterwards, so the list's order and the seven columns
 * the trainer is looking at agree. `addDay` already inserts in weekday order for the same reason,
 * and it matters twice over here: `Order` is assigned from array position at serialisation, and the
 * resulting `serverOrder` drift is what tells `pendingStructuralEdits` that a day moved.
 */
export function moveDayToWeekday(
  draft: MutablePlan,
  daySlotToken: string,
  targetWeekday: number,
): void {
  if (draft.mode !== 'date') return;
  if (!Number.isInteger(targetWeekday) || targetWeekday < 1 || targetWeekday > 7) return;

  const site = findDaySite(draft, daySlotToken);
  if (site === null) return;
  const from = site.day.dayOfWeek;
  if (from === targetWeekday) return;

  const occupant = site.phase.days.find(
    (day) => day.slotToken !== daySlotToken && day.dayOfWeek === targetWeekday,
  );
  // A day with no weekday at all has nothing to hand over, so a swap would push the occupant out of
  // the week entirely. It can only reach the canvas as overflow, and moving it is not this act.
  if (occupant !== undefined && from === null) return;
  if (occupant !== undefined) occupant.dayOfWeek = from;
  site.day.dayOfWeek = targetWeekday;

  // Nulls last. A date-based day always carries a weekday, so this only orders a tree this app did
  // not author — and putting the unplaceable days at the end keeps the seven columns in step with
  // the first seven entries.
  site.phase.days.sort((left, right) => (left.dayOfWeek ?? 8) - (right.dayOfWeek ?? 8));
}

/** The day occupying a weekday within one phase, or `null`. What a drop target reads to say "swap". */
export function dayOnWeekday(
  phase: PhaseDraft,
  weekday: number,
): DayDraft | null {
  return phase.days.find((day) => day.dayOfWeek === weekday) ?? null;
}

// ── The day, structurally ────────────────────────────────────────────────────────────────────

/**
 * Remove a training day, and everything programmed in it.
 *
 * Nothing refuses it, including a day the client has already trained (owner, 15 September 2026,
 * matching mobile and the backend). The backend withdraws the day's upcoming, not-started workouts;
 * the finished ones are frozen snapshots and stay in the client's history, and a trainer's booking
 * stays as an empty booked slot. Deleting a phase's LAST day is legal too: it leaves the phase
 * unserialisable and `firstEmptyPhaseName` says so at the Save button, the same treatment an emptied
 * day already gets.
 *
 * The day's `slotToken` goes with it, which is exactly what tells the backend "this day was deleted" apart
 * from "every later day shifted up one": the surviving days keep their own tokens and are matched by
 * them, so nothing else in the phase is disturbed.
 *
 * An unknown token is a no-op. A day the trainer confirmed a delete for may have been taken by an
 * undo between the prompt and the answer, and a delete that finds nothing has nothing to do.
 */
export function removeDay(draft: MutablePlan, daySlotToken: string): void {
  for (const phase of draft.phases) {
    const index = phase.days.findIndex((day) => day.slotToken === daySlotToken);
    if (index === -1) continue;
    phase.days.splice(index, 1);
    return;
  }
}

// ── The day, week by week (the calendar's gestures) ──────────────────────────────────────────
//
// Four writes over `DayDraft`'s three window fields. They are separate operations rather than one
// `setDayWindow(fields)` because each is a different act with a different undo entry and a different
// sentence in the trainer's ear, and because the DECISION about which one a gesture means is
// `dayCycleWindow.planDayCycleRemoval`'s — a mutator that also decided would have to be able to turn
// itself into a deletion halfway through.

/**
 * Cut one cycle out of a day's lifespan — the calendar's „Usuń z planu… · tylko ten tydzień".
 *
 * A plain hole. The ends are untouched, so the weeks on either side keep running, and the day's
 * slot token stays exactly where it was — which is what makes the client's other occurrences of
 * this day survive the edit (`WorkoutExecutionPropagator` matches by token).
 *
 * **No normalisation.** A hole that lands on an end is left where the trainer put it rather than
 * folded into a moved end: the backend stores the authored window verbatim and resolves it on read,
 * and tidying it here would mean two clients disagreeing about what the same plan says. A duplicate
 * hole is dropped, because the wire's list is a set and sending one cycle twice is our bug.
 */
export function excludeDayCycle(draft: MutablePlan, daySlotToken: string, cycle: number): void {
  if (!Number.isInteger(cycle) || cycle < 1 || cycle > MAX_ABSOLUTE_CYCLE) return;
  const day = findDay(draft, daySlotToken);
  if (day === null) return;
  if (day.excludedCycles.includes(cycle)) return;
  day.excludedCycles.push(cycle);
  day.excludedCycles.sort((left, right) => left - right);
}

/**
 * End a day's lifespan after `retirementCycle` — the calendar's „…ten tydzień i wszystkie kolejne".
 *
 * The caller passes the LAST cycle the day still runs in, not the first one it does not: the
 * gesture is about a week the trainer is looking at, and `planDayCycleRemoval` is what converts one
 * into the other so the off-by-one lives in a tested pure function rather than at each call site.
 *
 * An earlier retirement than the one stored is the only direction this can move — a later one would
 * be widening the day back over weeks it does not run in, which is `restoreDayCycle`'s job and has
 * to answer for the holes it sweeps over.
 */
export function retireDayFrom(
  draft: MutablePlan,
  daySlotToken: string,
  retirementCycle: number,
): void {
  if (!Number.isInteger(retirementCycle)) return;
  const day = findDay(draft, daySlotToken);
  if (day === null) return;
  day.retirementCycle = clampToPlanCycles(retirementCycle);
}

/**
 * Write a day's whole lifespan at once — the one write the ADD and RESTORE flows share.
 *
 * Both of them compute a complete window (`addedDayWindow`, `restoredDayWindow`) rather than a
 * patch, because both can move two ends and the holes between them in a single answer, and applying
 * that as three separate writes would record three undo entries for one decision.
 *
 * Field by field and only where it differs, for the reason `rebasePhaseWindows` gives: a
 * whole-object replace records a patch the size of the node and the undo has to apply its inverse.
 */
export function setDayCycleWindow(
  draft: MutablePlan,
  daySlotToken: string,
  window: DayCycleWindow,
): void {
  const day = findDay(draft, daySlotToken);
  if (day === null) return;
  const activation = window.activationCycle === null ? null : clampToPlanCycles(window.activationCycle);
  const retirement = window.retirementCycle === null ? null : clampToPlanCycles(window.retirementCycle);
  if (day.activationCycle !== activation) day.activationCycle = activation;
  if (day.retirementCycle !== retirement) day.retirementCycle = retirement;
  const holes = [...new Set(window.excludedCycles)]
    .filter((cycle) => Number.isInteger(cycle) && cycle >= 1 && cycle <= MAX_ABSOLUTE_CYCLE)
    .sort((left, right) => left - right);
  if (holes.join(',') !== day.excludedCycles.join(',')) day.excludedCycles = holes;
}

/**
 * Put a day back into one cycle — the `+` on a cell whose training the trainer had cut.
 *
 * The old row comes back WITH ITS CONTENT: its exercises, its slot tokens and therefore its
 * progression chain and its activation history. That is the whole difference between this and
 * `addDayOneTime`, and it is the owner's decision of 15 August 2026 — a trainer who removed a
 * training from one week and puts it back means the same training, not a new one that starts its
 * curve over.
 *
 * The window arithmetic — including closing the cycles a moved end sweeps over as holes — is
 * `restoredDayWindow`'s, because it is a decision with two answers and the answers have to be
 * comparable before the question is asked.
 */
export function restoreDayCycle(
  draft: MutablePlan,
  daySlotToken: string,
  cycle: number,
  scope: DayCycleScope,
): void {
  const window = restoredDayWindow(draft.phases, daySlotToken, cycle, scope);
  if (window === null) return;
  setDayCycleWindow(draft, daySlotToken, window);
}

/**
 * Carry out a removal `planDayCycleRemoval` decided on — the ONE entry point a caller taking a
 * training out of a week reaches for.
 *
 * The three writes are separate operations because they are three different edits with three
 * different undo entries; this exists because CHOOSING between them is not the caller's decision to
 * re-make. A component switching on the kind and dispatching one of three is the combination this
 * slice's rule says belongs behind an entry point a test can reach — and the switch has a fourth
 * arm (`nothing`) whose only correct handling is to do nothing at all, which is exactly the arm a
 * call site forgets.
 *
 * The decision is passed in rather than recomputed, so the answer the caller GATED on is the answer
 * that is applied: `removeDay` carries its own refusal, and re-deriving here could produce a
 * different kind against a tree that moved in between.
 */
export function applyDayCycleRemoval(
  draft: MutablePlan,
  daySlotToken: string,
  removal: DayCycleRemoval,
): void {
  if (removal.kind === 'excludeCycle') excludeDayCycle(draft, daySlotToken, removal.cycle);
  else if (removal.kind === 'retireFrom') retireDayFrom(draft, daySlotToken, removal.retirementCycle);
  else if (removal.kind === 'removeDay') removeDay(draft, daySlotToken);
}

/**
 * Add a SECOND training on a weekday that already has one, running in exactly one cycle.
 *
 * `addDay` refuses a duplicate weekday, and it is right to: the builder's week canvas has one
 * column per weekday and can only report a second day as overflow. This variant exists because the
 * calendar can express what the canvas cannot — a cell is one (weekday, cycle) pair, so two rows on
 * one weekday are two different weeks and never two cards in one square. Owner decision, 15 August
 * 2026: a `+` on a cell whose weekday already carries a holed or retired row offers this beside
 * restoring the old one.
 *
 * The window is `{N, N}` at construction rather than at save, and that is what makes it safe: a
 * duplicate weekday is only ever legal because the two rows run in different cycles, so a row that
 * reached the tree open-ended would collide with its sibling in every week of the phase. It is also
 * why this asks no scope question — one-time is what it IS, not an answer the trainer gives.
 *
 * **Known cost, and it is the builder's:** `weekProjection` carries the second row as overflow, so
 * the builder's week canvas counts it and names it in a banner but draws no card for it. It is
 * visible and editable from the calendar, which is the surface that created it. See the slice's
 * `AGENTS.md`.
 */
export function addDayOneTime(
  draft: MutablePlan,
  phaseIndex: number,
  dayOfWeek: number,
  cycle: number,
): void {
  const phase = draft.phases[phaseIndex];
  if (phase === undefined) return;
  if (draft.mode !== 'date') return;
  if (!Number.isInteger(dayOfWeek) || dayOfWeek < DAY_OF_WEEK_RANGE.min || dayOfWeek > DAY_OF_WEEK_RANGE.max) {
    return;
  }
  if (!Number.isInteger(cycle) || cycle < 1 || cycle > MAX_ABSOLUTE_CYCLE) return;
  if (phase.days.length >= COLLECTION_CAPS.daysPerPhase.max) return;

  const day = createDay(draft.mode, dayOfWeek) as Draft<DayDraft>;
  day.activationCycle = cycle;
  day.retirementCycle = cycle;
  // Inserted AFTER the last day on the same weekday, so the array order and the seven columns still
  // agree and the sibling keeps the position its `serverOrder` was captured at.
  const at = phase.days.findIndex((existing) => (existing.dayOfWeek ?? 0) > dayOfWeek);
  if (at !== -1) phase.days.splice(at, 0, day);
  else phase.days.push(day);
}

/**
 * The token the just-run `addDayOneTime` minted, so the caller can open the dock on it.
 *
 * Read back from the tree for the reason `addedDayToken` gives — a mutator returns nothing — and it
 * cannot reuse that function: `addedDayToken` finds the FIRST day on the weekday, which after a
 * one-time add is the row that was already there.
 */
export function addedOneTimeDayToken(
  draft: PlanDraft,
  phaseIndex: number,
  dayOfWeek: number,
  cycle: number,
): string | null {
  const phase = draft.phases[phaseIndex];
  if (phase === undefined) return null;
  const match = phase.days.filter(
    (day) =>
      day.dayOfWeek === dayOfWeek &&
      day.serverId === null &&
      day.activationCycle === cycle &&
      day.retirementCycle === cycle,
  );
  return match[match.length - 1]?.slotToken ?? null;
}

/**
 * Why this day may not be copied, or `null` when it may.
 *
 * · `phaseFull` / `closedToNewDays` — `dayAdditionRefusal`'s, because a copy IS a new day: a fresh
 *   slot token the client's record has never seen. It used to refuse any phase whose first cycle
 *   was consumed; that phase lock was withdrawn on 15 September 2026, because the backend accepts a
 *   new day there and only a phase with no open cycle left would leave the copy unscheduled.
 * · `weekFull` — a DATE plan places a day on a weekday and this app authors at most one day per
 *   weekday (`addDay` refuses a second, `weekProjection` can only report it as overflow), so a phase
 *   already using all seven has nowhere to put the copy. **This is a deliberate divergence from the
 *   phone**, whose `duplicateDay` copies `dayOfWeek` verbatim and cheerfully puts two days on one
 *   Monday; the weekly grid cannot draw that, and the copy would land in the overflow banner rather
 *   than on screen.
 * A SELF-PACED phase adds no refusal of its own beyond the two above. Mobile's `addDayGate` refused
 * a copy in any but the last phase of a running tape fork, because a day there lengthened the
 * phase's cycle in days and pushed every later occurrence down the calendar. R2 deletes that
 * arithmetic and R18 states the result: no occurrence after the cursor has a date, so the copy's
 * blast radius is the cycle the trainer clicked. See `structuralEditFlow`'s header.
 */
export type DayDuplicationRefusal = DayAdditionRefusal | 'weekFull';

export function dayDuplicationRefusal(
  draft: PlanDraft,
  daySlotToken: string,
): DayDuplicationRefusal | null {
  for (const [phaseIndex, phase] of draft.phases.entries()) {
    if (!phase.days.some((day) => day.slotToken === daySlotToken)) continue;
    const addition = dayAdditionRefusal(draft, phaseIndex);
    if (addition !== null) return addition;
    if (draft.mode === 'date') {
      return freeWeekday(phase) === null ? 'weekFull' : null;
    }
    // A self-paced copy is appended after its source and needs no free coordinate — there is no
    // weekday to be taken.
    return null;
  }
  return null;
}

/**
 * The lowest weekday (1–7) this phase has no day on, or `null` when all seven are taken.
 *
 * The LOWEST rather than the nearest free weekday after the source, because the copy is a new
 * training the trainer is about to programme rather than a repetition of the source's own slot —
 * putting it at the start of the free week is the answer they can find, and the card is one drag
 * from anywhere else.
 */
function freeWeekday(phase: Pick<PhaseDraft, 'days'>): number | null {
  const taken = new Set(phase.days.map((day) => day.dayOfWeek));
  for (let weekday = DAY_OF_WEEK_RANGE.min; weekday <= DAY_OF_WEEK_RANGE.max; weekday += 1) {
    if (!taken.has(weekday)) return weekday;
  }
  return null;
}

/**
 * Copy a training day, with everything programmed in it — a **NEW SLOT**, and a new slot for every
 * exercise beneath it (`cloneDay`, document-model ADR §3).
 *
 * That is the whole reason this operation exists rather than being a splice a component could write:
 * a copy sharing the source's tokens inherits its progression chain, its `PreviousExecution` history
 * and its activation cycle, and it does so SILENTLY — the trainer has no reason to believe the copy
 * carries any of it.
 *
 * ── Where the copy lands ─────────────────────────────────────────────────────────────────────
 * · **SEQUENCE** — directly after its source, carrying the source's own `restDaysAfter`. Mobile's
 *   placement exactly, and the one a trainer means by "duplicate this training": the copy repeats
 *   the block it was taken from rather than appearing at the end of the cycle.
 * · **DATE** — on the first free weekday, inserted in weekday order so the array and the seven
 *   columns agree. It cannot share the source's weekday (see `dayDuplicationRefusal`), so a
 *   placement decision is unavoidable and this is the one the trainer can find.
 *
 * Guarded by `dayDuplicationRefusal`; an unknown token is a no-op.
 */
export function duplicateDay(draft: MutablePlan, daySlotToken: string): void {
  if (dayDuplicationRefusal(draft, daySlotToken) !== null) return;
  for (const phase of draft.phases) {
    const index = phase.days.findIndex((day) => day.slotToken === daySlotToken);
    const source = phase.days[index];
    if (index === -1 || source === undefined) continue;

    // Cast at the construction seam — see `addExercise`. `cloneDay` returns the app's own read-only
    // shape; the producer is about to adopt it.
    const copy = cloneDay(source) as Draft<DayDraft>;
    if (draft.mode !== 'date') {
      phase.days.splice(index + 1, 0, copy);
      return;
    }

    const weekday = freeWeekday(phase);
    if (weekday === null) return;
    copy.dayOfWeek = weekday;
    const at = phase.days.findIndex((existing) => (existing.dayOfWeek ?? 0) > weekday);
    if (at !== -1) phase.days.splice(at, 0, copy);
    else phase.days.push(copy);
    return;
  }
}

// ── Phases ───────────────────────────────────────────────────────────────────────────────────
//
// The one level where POSITION carries identity: `PhaseGrowthPolicy` judges a submitted phase's `Id`
// and position against the consumed prefix, and `plan_phase_orders_not_contiguous` requires exactly
// `1..N`. `draftToRequest` emits `order: index + 1`, so the array's order IS the wire's — which
// means every operation below is a statement about the consumed prefix, and each one asks
// `phaseEdits` rather than judging for itself.

/**
 * A copied phase: a phase the server has never seen, with a **NEW SLOT** for every day and every
 * exercise inside it.
 *
 * `serverId` is dropped, which is what stops the copy standing in for the phase a trained cycle was
 * recorded against — the backend's own note: "a copy or a newly added phase cannot stand in for
 * one". `startCycle` is restamped by the operation, because a phase does not know where it sits.
 */
export function clonePhase(phase: PhaseDraft): PhaseDraft {
  return { ...phase, serverId: null, days: phase.days.map(cloneDay) };
}

/**
 * Rewrite every phase's `startCycle` from the tree — and **carry every per-cycle override in that
 * phase along with the window it just moved.** After ANY edit that changes how many cycles come
 * before a phase, or how many a phase itself runs.
 *
 * `PhaseDraft.startCycle` is captured from the server at load and its own doc says "the store
 * recomputes it after a structural edit". Nothing edited a phase until now, so nothing did; the
 * moment one does, the field is what the phase band's cycle ranges, the phase echo and
 * `isPhaseLocked` all read, and a stale one puts the lock on the wrong phase.
 *
 * ── Why the overrides move with it, and why the rule lives HERE ───────────────────────────────
 * `ProgressionOverride.cycleIndex` is ABSOLUTE across the plan, never phase-relative, and
 * `TemplateSetMaterializer` refuses the WHOLE save when one falls outside its phase's cycles —
 * "A set override's CycleIndex {n} is outside this phase's cycles {start}..{last}", thrown with no
 * refusal code, so `saveErrorCopy` cannot word it and the trainer meets a bare 400 they cannot act
 * on. Three edits reach that state and every one of them is a phase operation: copying a phase (the
 * copy's pins would still point at the SOURCE's cycles), shrinking one (a pin past the new end), and
 * resizing, removing or copying an EARLIER phase (every later window slides while the pins stand
 * still). One pass, one invariant: **an exercise's pins belong to its phase's window and travel with
 * it.**
 *
 * So the delta is the phase's own — `newStart − oldStart` — applied to every override beneath it,
 * and a pin left past the new last cycle is DROPPED rather than clamped onto a cycle the trainer did
 * not pin. Dropping is honest and it is not destructive in practice: the whole pass is inside the
 * operation's producer, so Cmd+Z restores every dropped pin with the phase length that took it.
 *
 * A COPY needs no special case, which is the point of expressing it as a window delta:
 * `clonePhase` carries the source's `startCycle`, so the copy's delta is exactly the source's length
 * and its pins land on the same cycle OF ITS OWN WINDOW that they held in the source's.
 *
 * Written FIELD BY FIELD and only where it differs, so a rename records a rename-sized patch rather
 * than a whole-array replace — the same reason `refreshRestColumns` writes the way it does.
 */
function rebasePhaseWindows(draft: MutablePlan): void {
  const starts = phaseStartCycles(draft.phases);
  draft.phases.forEach((phase, index) => {
    const start = starts[index] ?? 1;
    const delta = start - phase.startCycle;
    if (delta !== 0) phase.startCycle = start;
    const cycles = Math.max(1, phase.cycles);
    const last = start + cycles - 1;
    for (const day of phase.days) {
      // The DAY owns all three parts of a lifespan; an exercise owns two, because its activation is
      // server-resolved (`ExerciseDraft.activationCycle`). Writing the day's here and nowhere else is
      // what makes that difference unmissable rather than a flag one of them is passed.
      if (day.activationCycle !== null) {
        day.activationCycle = carriedStart(day.activationCycle + delta, start, last);
      }
      carryEndAndHolesIntoPhase(day, delta);
      for (const exercise of day.exercises) {
        carryEndAndHolesIntoPhase(exercise, delta);
        carryOverridesIntoWindow(exercise, delta, start, cycles);
      }
    }
  });
}

/**
 * Where a moved START lands: absent when it no longer constrains, clamped when it would empty the
 * window, and otherwise exactly where the shift put it.
 *
 * **This is the one coordinate that is normalised against the PHASE rather than the plan, and it is
 * a deferred product question rather than a settled rule.** `carryEndAndHolesIntoPhase` keeps an
 * out-of-span end and its holes verbatim, because the backend stores and ignores them; the activation
 * cannot follow it there, because an activation past the phase's last cycle leaves the day's window
 * meeting no cycle at all and `PlanWindowPolicy.EnsureNoWindowIsEmpty` refuses the whole save with
 * `plan_window_empty` — the refusal has to be answered somewhere, and answering it by clamping is
 * what shipped. What the owner has not been asked is whether a shrink should instead REFUSE, so the
 * trainer is told a day is about to move rather than finding it moved.
 *
 * An earlier revision named `plan_cycle_uninhabited` here. That code is SEQUENCE-ONLY —
 * `EnsureEveryCycleIsInhabited` returns immediately for any other mode — so it cannot be the answer
 * to a day window at all, and it is not reachable from this app's window edits. Corrected against
 * `PlanWindowPolicy.cs` on 15 August 2026.
 */
function carriedStart(moved: number, phaseStartCycle: number, phaseLastCycle: number): number | null {
  if (moved < phaseStartCycle) return null;
  return moved > phaseLastCycle ? phaseLastCycle : moved;
}

/**
 * Carry the two lifespan parts a day and an exercise BOTH own — the retirement and the holes — along
 * with the phase whose coordinates they are expressed in.
 *
 * **The window is PLAN-ABSOLUTE, so a phase edit moves the ground under it.** This is the same
 * hazard `carryOverridesIntoWindow` exists for, one field set over: `activationCycle`,
 * `retirementCycle` and `excludedCycles` are 1-based across the whole plan, so resizing, removing or
 * copying an earlier phase slides every later phase's span while the windows inside it stand still.
 * A day authored "from cycle 9 onward" in a phase that starts at 9 becomes a day starting in the
 * middle of a phase that now starts at 7.
 *
 * **This is a deliberately non-minimal change, and here is why it cannot wait for the gestures that
 * author a window.** The windows are READ and RE-EMITTED as of this round, so they are already live
 * data on every save — a stale coordinate is not a display slip, it is a wipe: the backend
 * re-materialises the client's not-started workouts from exactly these numbers. The existing phase
 * edits are what move them, and those shipped weeks ago. `phaseWindowShifts` in the structural
 * preview stays truthful for free, because it measures the phase coordinates this pass restamps.
 *
 * ── A coordinate that leaves the phase's span is KEPT, and that is deliberate ─────────────────
 * Only a phase whose own LENGTH shrank can push one out; a slide moves the span and its contents by
 * the same delta, so nothing escapes that way. For the ones that do, this pass does nothing at all,
 * and the reason is that **the backend already answers the question, better than the client can.**
 *
 * `PostDayTemplateInput.ExcludedCycles` says it in the contract's own words — "entries outside the
 * resolved ends are ignored rather than refused: a window is authored against a host that may since
 * have shrunk" — and `PlanTree` does the same for the end bound, which
 * `AnExerciseWindowNeverReachesOutsideItsPhase` pins: a retirement of 9 inside a two-cycle phase
 * resolves to a span of 1–2 and refuses nothing. So an out-of-span bound is STORED and IGNORED, and
 * it starts constraining again the moment the phase grows back over it.
 *
 * An earlier revision nulled the retirement and spliced the holes out. It read as tidying and it was
 * a loss: shrink a phase, look at it, grow it back, and the weeks the trainer had cut were gone —
 * client-side only, with the server still holding them until the next save overwrote them with the
 * widened tree. Nulling a bound is never neutral here, because an absent bound means "the whole
 * host": it ADDS training rather than losing a note about it.
 *
 * What is still refused, and therefore still enforced here, is the **PLAN's** own range: the wire
 * takes cycles 1..`MAX_ABSOLUTE_CYCLE` and `ValidateExcludedCycles` 400s on anything outside it. A
 * negative delta can only push a coordinate below 1 on a tree that already contradicted itself, but
 * a 400 the trainer cannot act on is the wrong way to find that out — so a hole outside the plan is
 * dropped and an end outside it is clamped to the edge. Never nulled: see above.
 *
 * The activation is NOT here. It is the day's alone (an exercise's is server-resolved) and it is
 * clamped rather than carried, because an activation past the phase's end would leave the day
 * prescribing nothing and `EnsureNoWindowIsEmpty` refuses the whole save with `plan_window_empty`.
 * See `carriedStart`, which also records the code this comment used to name wrongly.
 *
 * Mutated in place, field by field and only where it differs, for the reason `rebasePhaseWindows`
 * gives: a whole-object replace records a patch the size of the node, and the undo has to apply its
 * inverse.
 */
function carryEndAndHolesIntoPhase(node: Draft<DayDraft> | Draft<ExerciseDraft>, delta: number): void {
  if (delta === 0) return;
  if (node.retirementCycle !== null) {
    node.retirementCycle = clampToPlanCycles(node.retirementCycle + delta);
  }
  for (let index = node.excludedCycles.length - 1; index >= 0; index -= 1) {
    const cycle = node.excludedCycles[index];
    if (cycle === undefined) continue;
    const moved = cycle + delta;
    if (moved < 1 || moved > MAX_ABSOLUTE_CYCLE) node.excludedCycles.splice(index, 1);
    else node.excludedCycles[index] = moved;
  }
}

/**
 * Slide one exercise's pins by `delta` and drop whatever falls outside the window the BACKEND will
 * materialise the exercise over.
 *
 * Walked backwards and mutated in place rather than rebuilt: a whole-array replace would record a
 * patch the size of every pin the exercise carries on every phase edit, and the inverse of that is
 * what an undo has to apply.
 *
 * ── The floor is the ACTIVATION, not the phase start, and that is where this got it wrong ─────
 * An earlier revision dropped only the cycles ABOVE the window, on the reasoning that a pin below an
 * exercise's activation is a coordinate this pass cannot produce and that the backend re-assigns
 * activation anyway. **Both halves were false and both are withdrawn.**
 *
 * `ExerciseActivationPlan.Capture` reads the activation off the PRE-EDIT tree and `ApplyTo` does
 * `Math.Clamp(resolved, phase.Start, phase.End)` — it carries the old number and clamps it, it does
 * not recompute one. `RematerializeSets` is then handed that activation as the window's own start,
 * and `ApplyAuthoredOverrides` refuses any override below it with the same uncoded
 * `ValidationException` a pin past the end gets. So the reachable case is a NEGATIVE delta: a phase
 * ahead of the client shrinks, this phase slides back, its pins slide with it — and the activation
 * does not, because the clamp leaves a mid-window activation exactly where it was. The pins land
 * under it and the whole save is refused with a 400 the trainer cannot act on. Worse, the matrix
 * draws the window from the same activation, so the pins are invisible as well as unsavable.
 *
 * A POSITIVE delta cannot reach it: every pin was at or above the old floor and moves by the same
 * `delta` the floor can move by at most, so the ordering is preserved.
 *
 * `exerciseCycleWindow` IS the clamp, in this app's own vocabulary, so the floor is asked for rather
 * than recomputed — and the two therefore cannot drift from the backend's.
 *
 * **`activationCycle` is deliberately NOT rebased.** Writing `old + delta` here would make this app's
 * activation and the backend's `clamp(old)` disagree on every negative delta, and the tree the
 * trainer sees would then be materialised against a different origin than the one it was authored
 * over. Following the clamp is the only reading that keeps the two the same number.
 */
function carryOverridesIntoWindow(
  exercise: Draft<ExerciseDraft>,
  delta: number,
  phaseStartCycle: number,
  phaseCycleCount: number,
): void {
  if (exercise.progressionOverrides.length === 0) return;
  const window = exerciseCycleWindow(exercise.activationCycle, phaseStartCycle, phaseCycleCount);
  const lastCycle = window.startCycle + window.cycleCount - 1;

  for (let index = exercise.progressionOverrides.length - 1; index >= 0; index -= 1) {
    const override = exercise.progressionOverrides[index];
    if (override === undefined) continue;
    if (delta !== 0) override.cycleIndex += delta;
    if (override.cycleIndex < window.startCycle || override.cycleIndex > lastCycle) {
      exercise.progressionOverrides.splice(index, 1);
    }
  }
}

/**
 * Append a phase — the band's "+ Faza", in either kind.
 *
 * **Appended at the TAIL and nowhere else.** A phase inserted anywhere before the consumed boundary
 * slides every later phase one position down, and the policy compares position for position, so the
 * client's trained cycles would be judged against the wrong block. The tail is the one position that
 * cannot disturb the prefix, and inserting elsewhere is a reorder — which this round does not build.
 *
 * The NAME arrives as a parameter: `model/` holds no sentences, and "Faza 4" is copy the page
 * resolves. It runs at one cycle (`ADDED_PHASE_CYCLES`) with one empty day, so it is unsavable until
 * the trainer puts an exercise in it — which the Save button already explains by name.
 */
export function addPhase(draft: MutablePlan, type: PhaseType, name: string): void {
  if (addPhaseRefusal(draft) !== null) return;
  const trimmed = name.trim();
  if (trimmed.length === 0) return;
  // Cast at the construction seam — see `addExercise`.
  draft.phases.push(
    createPhase(draft.mode, trimmed, 1, type, ADDED_PHASE_CYCLES) as Draft<PhaseDraft>,
  );
  rebasePhaseWindows(draft);
}

/**
 * Copy a phase and everything in it, directly after its source.
 *
 * Mobile's placement, and the one "duplicate this block" means: the copy repeats where it came from
 * rather than landing at the end of the plan. `phaseEditGates(...).duplication` is what makes that
 * safe — it refuses a source that closed before the consumed boundary, because inserting after such
 * a phase pushes the block the client is training now one position down.
 */
export function duplicatePhase(draft: MutablePlan, phaseIndex: number, name: string): void {
  if (phaseEditGates(draft, phaseIndex)?.duplication != null) return;
  const source = draft.phases[phaseIndex];
  if (source === undefined) return;
  const trimmed = name.trim();
  if (trimmed.length === 0) return;
  // Cast at the construction seam — see `addExercise`.
  const copy = clonePhase(source) as Draft<PhaseDraft>;
  copy.name = trimmed;
  draft.phases.splice(phaseIndex + 1, 0, copy);
  rebasePhaseWindows(draft);
}

/**
 * Delete a phase, and every day and exercise in it.
 *
 * Guarded by `phaseEditGates(...).removal`: a plan keeps at least one phase, and a phase reaching
 * into the consumed prefix cannot go at all — the client's finished sessions are recorded against it.
 */
export function removePhase(draft: MutablePlan, phaseIndex: number): void {
  if (phaseEditGates(draft, phaseIndex)?.removal != null) return;
  if (draft.phases[phaseIndex] === undefined) return;
  draft.phases.splice(phaseIndex, 1);
  rebasePhaseWindows(draft);
}

/**
 * Rename a phase.
 *
 * **Whitespace is refused rather than stored.** `PostPhaseTemplateInput.Name` is `[MinLength(1)]`, so
 * an empty name is a 400 on a field the trainer emptied deliberately; the editor reverts to what was
 * there, which is the same shape the plan's own rename takes. Free on a locked phase — renaming
 * changes no cycle's covering, and `PhaseGrowthPolicy` says so in as many words.
 */
export function setPhaseName(draft: MutablePlan, phaseIndex: number, name: string): void {
  const phase = draft.phases[phaseIndex];
  if (phase === undefined) return;
  const trimmed = name.trim();
  if (trimmed.length === 0) return;
  phase.name = trimmed;
}

/**
 * Switch a phase between Build and Deload.
 *
 * Refused inside the consumed prefix: the sessions the client has already finished are labelled with
 * the phase's type, and `plan_phase_structure_locked` is what a save would answer.
 */
export function setPhaseType(draft: MutablePlan, phaseIndex: number, type: PhaseType): void {
  const phase = draft.phases[phaseIndex];
  if (phase === undefined || phase.type === type) return;
  if (phaseEditGates(draft, phaseIndex)?.retype != null) return;
  phase.type = type;
}

/**
 * Set how many cycles a phase runs.
 *
 * **Clamped into `phaseEditGates(...).cycles`, not refused** — the same shape `setRestDaysAfter`
 * takes, and for the same reason: the stepper cannot leave the range, so a value that does came from
 * a typed field or a stale render, and landing on the nearest legal number is what the trainer meant.
 * The bounds carry the consumed prefix's shrink floor, a completed phase's frozen length and the
 * plan-wide cycle cap, so nothing here re-derives any of the three.
 *
 * A non-integer is refused outright rather than rounded: `CycleCount` is an integer on the wire and
 * a silent round is a number the trainer did not type.
 */
export function setPhaseCycles(draft: MutablePlan, phaseIndex: number, cycles: number): void {
  if (!Number.isInteger(cycles)) return;
  const phase = draft.phases[phaseIndex];
  const gates = phaseEditGates(draft, phaseIndex);
  if (phase === undefined || gates === null) return;
  const next = Math.min(Math.max(cycles, gates.cycles.min), gates.cycles.max);
  if (phase.cycles === next) return;
  phase.cycles = next;
  rebasePhaseWindows(draft);
}

// ── The plan's own fields ────────────────────────────────────────────────────────────────────

/**
 * Rename the plan.
 *
 * **Whitespace is refused, unlike a session's name, and the asymmetry is the wire's.**
 * `PostSessionTemplateInput.Name` has a fallback chain behind it, so clearing a session's name is a
 * legal way to say "no name"; `PostPlanTemplateRequest.Name` is `[MinLength(1)]` with nothing to fall
 * back to, so an empty plan name is a 400 the trainer would meet on their next save. The editor
 * reverts to what was there rather than storing a name they never typed.
 *
 * The 200-character ceiling is enforced at the INPUT (`TEXT_LIMITS.planName`), where the trainer can
 * see the answer — never truncated here, because silently shortening a name is data loss dressed as
 * safety.
 */
export function setPlanName(draft: MutablePlan, name: string): void {
  const trimmed = name.trim();
  if (trimmed.length === 0 || trimmed.length > TEXT_LIMITS.planName.max) return;
  draft.name = trimmed;
}

/**
 * Set the plan's category.
 *
 * **It no longer writes a cover, and it no longer has to.** Until 15 August 2026 this operation
 * carried owner rule D3c — every plan has a cover, and a cover belongs to its plan's category — by
 * restamping `coverImageKey` in the same patch whenever the stored key left the new pool. The
 * contract then replaced that key with a `coverId` into a server-side pool this app cannot read, so
 * there is no key to restamp and nothing here may invent one.
 *
 * The rule still holds, and it holds for FREE: a plan with no server cover falls back to its
 * category's own deterministic pick at DISPLAY time (`planCover.ts`), so a strength plan switched to
 * Cardio changes its photograph the moment the chip is pressed, with no field written. A plan that
 * DOES carry a `coverId` keeps the cover the trainer chose on the phone, which is the honest answer:
 * this app has no standing to discard a deliberate pick it cannot see the pool of.
 *
 * `setPlanCoverKey` is gone with it. It refused a key outside the pool, and there is no longer a key.
 */
export function setPlanCategory(draft: MutablePlan, category: PlanCategoryKey): void {
  if (planCategoryKey(draft.category) === category) return;
  draft.category = planCategoryToApi(category);
}

/* ── Pasting a copied training into another week ─────────────────────────────────────────────── */

/** Why a paste cannot land where it was aimed. `null` is a paste that may go ahead. */
export type PasteRefusal = 'unknownDay' | 'noPhaseThere' | 'notDateBased' | 'phaseFull';

/**
 * **A copied training, landed on one square.**
 *
 * It is `cloneDay` followed by `addDayOneTime`'s window, and it is ONE operation rather than a
 * caller stitching those two together, for the reason every rule in this file is here: the two
 * halves are only correct in combination, and a caller that took the clone and forgot the window
 * would write an open-ended row that collides with its sibling in every week of the phase.
 *
 * ── A copy inherits NOTHING the original earned ───────────────────────────────────────────────
 * `cloneDay` mints a fresh slot token for the day AND for every exercise under it. That is the
 * whole point: a slot token is what the backend matches a progression chain and an activation
 * history by, so a copy carrying the source's token would silently pick up the original's
 * progression and the weeks it has already been trained in. The day's server identity goes with it,
 * because a copy has never been stored anywhere.
 *
 * ── It lands as a ONE-OFF, and that is not a default to be relaxed ────────────────────────────
 * The window is `{cycle, cycle}` — the same shape `addDayOneTime` writes and for the same reason:
 * the weekday it lands on may already carry a training, and an open-ended second row on one weekday
 * collides with its sibling in every week of the phase. One-time is what a pasted copy IS, not an
 * answer the trainer gives; a caller that wants it to reach later weeks asks the scope question and
 * widens the window afterwards.
 *
 * ── Known cost, and it is the builder's ───────────────────────────────────────────────────────
 * A paste onto an occupied weekday puts two rows on one weekday, which the builder's week canvas
 * cannot draw. `weekProjection` carries the second as overflow, counts it in the load and names it
 * in a banner with no card — the same cost `addDayOneTime` already documents.
 */
export function pasteDayAt(
  draft: MutablePlan,
  sourceDaySlotToken: string,
  target: { readonly dayOfWeek: number; readonly cycleIndex: number },
): void {
  if (pasteRefusal(draft, sourceDaySlotToken, target) !== null) return;

  const source = draft.phases
    .flatMap((phase) => phase.days)
    .find((day) => day.slotToken === sourceDaySlotToken);
  if (source === undefined) return;
  const site = phaseAtCycle(draft.phases, target.cycleIndex);
  if (site === null) return;
  const phase = draft.phases[site.index];
  if (phase === undefined) return;

  const copy = cloneDay(source) as Draft<DayDraft>;
  copy.dayOfWeek = target.dayOfWeek;
  copy.activationCycle = target.cycleIndex;
  copy.retirementCycle = target.cycleIndex;
  copy.excludedCycles = [];
  // Inserted AFTER the last day on the same weekday, so the array order and the seven columns still
  // agree — `addDayOneTime`'s own placement, and for its reason.
  const at = phase.days.findIndex((existing) => (existing.dayOfWeek ?? 0) > target.dayOfWeek);
  if (at !== -1) phase.days.splice(at, 0, copy);
  else phase.days.push(copy);
}

/**
 * Whether one paste may land, decided BEFORE anything is written.
 *
 * Separate from the operation because the caller pre-checks the whole paste — every target, all or
 * nothing — and a refusal discovered halfway through an Immer produce would leave half a paste in
 * the tree with no way to name what went in.
 */
export function pasteRefusal(
  draft: PlanDraft,
  sourceDaySlotToken: string,
  target: { readonly dayOfWeek: number; readonly cycleIndex: number },
): PasteRefusal | null {
  if (draft.mode !== 'date') return 'notDateBased';
  const source = draft.phases
    .flatMap((phase) => phase.days)
    .find((day) => day.slotToken === sourceDaySlotToken);
  if (source === undefined) return 'unknownDay';
  if (
    !Number.isInteger(target.dayOfWeek) ||
    target.dayOfWeek < DAY_OF_WEEK_RANGE.min ||
    target.dayOfWeek > DAY_OF_WEEK_RANGE.max
  ) {
    return 'noPhaseThere';
  }
  if (!Number.isInteger(target.cycleIndex) || target.cycleIndex < 1) return 'noPhaseThere';
  const site = phaseAtCycle(draft.phases, target.cycleIndex);
  if (site === null) return 'noPhaseThere';
  const phase = draft.phases[site.index];
  if (phase === undefined) return 'noPhaseThere';
  // `daysPerPhase` is the backend's own `[MaxLength(14)]`, and it counts ROWS rather than weekdays:
  // a phase already holding fourteen rows refuses a fifteenth whatever weekday it lands on.
  if (phase.days.length >= COLLECTION_CAPS.daysPerPhase.max) return 'phaseFull';
  return null;
}

/**
 * How many more rows a phase can take — what a multi-day paste pre-checks its own size against.
 *
 * A paste of three into a phase with one row left is refused whole; asking `pasteRefusal` three
 * times would pass all three, because nothing is written between the questions.
 */
export function phaseDayHeadroom(draft: PlanDraft, cycleIndex: number): number {
  const site = phaseAtCycle(draft.phases, cycleIndex);
  if (site === null) return 0;
  const phase = draft.phases[site.index];
  if (phase === undefined) return 0;
  return Math.max(0, COLLECTION_CAPS.daysPerPhase.max - phase.days.length);
}

// ── Whole items arriving already built (ai-plan-builder R15) ─────────────────────────────────
//
// A proposal's after-states are complete items: an exercise with its sets and its rule, a day, a
// phase with everything in it. Each of the three below puts one such item into the tree with the
// same housekeeping its library-pick sibling does, and answers whether it did. They mint nothing:
// the item's slot tokens were minted by the server for this proposal (`ai-p{turn}.{n}`), and minting
// again here would leave the proposal's own dependencies pointing at tokens that do not exist.

/**
 * Put a built exercise into a day: at `at` (an array position), or — `null` — where a library pick
 * would land, a warm-up after the day's leading warm-ups and anything else at the end. Refused on a
 * day that is not there or is full. The day's groups and rest columns are derived again, because an
 * arriving member can close a superset round.
 */
export function insertExerciseDraft(
  draft: MutablePlan,
  daySlotToken: string,
  exercise: ExerciseDraft,
  at: number | null,
): boolean {
  const day = findDay(draft, daySlotToken);
  if (day === null || day.exercises.length >= COLLECTION_CAPS.exercisesPerSession.max) return false;
  let index = at ?? day.exercises.length;
  if (at === null && isWarmupRole(exercise.role) && !exercise.group) {
    index = 0;
    while (index < day.exercises.length && isStandaloneDayWarmup(day.exercises[index])) index += 1;
  }
  // Cast at the construction seam — see `addExercise`.
  day.exercises.splice(Math.min(Math.max(index, 0), day.exercises.length), 0, exercise as Draft<ExerciseDraft>);
  normaliseGroups(day);
  refreshRestColumns(day);
  return true;
}

/**
 * Put a built day into the phase at `phaseIndex`. On a date plan it goes after the last day on its
 * weekday or before it, so the array and the seven columns agree (`pasteDayAt`'s placement); on a
 * self-paced plan at the end. Guarded by `dayAdditionRefusal`, like every other way a day arrives.
 */
export function insertDayDraft(draft: MutablePlan, phaseIndex: number, day: DayDraft): boolean {
  const phase = draft.phases[phaseIndex];
  if (phase === undefined || dayAdditionRefusal(draft, phaseIndex) !== null) return false;
  // Cast at the construction seam — see `addExercise`.
  const arriving = day as Draft<DayDraft>;
  const weekday = draft.mode === 'date' ? day.dayOfWeek : null;
  const at = weekday === null ? -1 : phase.days.findIndex((existing) => (existing.dayOfWeek ?? 0) > weekday);
  if (at !== -1) phase.days.splice(at, 0, arriving);
  else phase.days.push(arriving);
  return true;
}

/**
 * Put a built phase at array position `at`. Refused past the phase cap, and before any phase the
 * client has begun: every phase from `at` on moves one position down, and `PhaseGrowthPolicy` judges
 * a submitted phase against the consumed prefix position for position. Every phase's window is
 * recomputed afterwards, carrying the later phases' pins with them (`rebasePhaseWindows`).
 */
export function insertPhaseDraft(draft: MutablePlan, at: number, phase: PhaseDraft): boolean {
  if (at < 0 || at > draft.phases.length) return false;
  if (draft.phases.length >= COLLECTION_CAPS.phasesPerPlan.max) return false;
  const consumed = isLivePlan(draft.live) ? consumedCycleCount(draft.live) : 0;
  const cyclesBefore = draft.phases.slice(0, at).reduce((total, existing) => total + existing.cycles, 0);
  if (cyclesBefore < consumed) return false;
  // Cast at the construction seam — see `addExercise`.
  draft.phases.splice(at, 0, phase as Draft<PhaseDraft>);
  rebasePhaseWindows(draft);
  return true;
}
